import { OfferCues } from './offer-cues.js';
import { AD_REFILL_TIMEOUT_MS, AD_REQUEST_BLOCKING_TIMEOUT_MS, AD_REQUEST_LEAD_SEC, AD_REQUEST_TIMEOUT_MS, breakDurationSec, isLiveOpen, liveEffectiveWindow, livePollIntervalMs, liveWindowAt, midrollCuePoints, transitionBreakKind, } from '@apm/contracts';
import { EventQueue } from './event-queue.js';
import { LIVE_PREPARE_RETRY_MS, liveArmAllowed, liveBeforePlan, recordedBreakQuiet, recordedLockedByLiveAd, liveEntryPlan, } from './live-entry.js';
import { PlaylistClient } from './playlist.js';
import { seekTarget } from './seek-policy.js';
import { canSkip, secondsUntilSkippable } from './skip-policy.js';
import { DEFAULT_PREROLL_SEC } from './types.js';
/**
 * 이 기기의 식별자를 두는 자리.
 *
 * **값을 바꾸면 기존 설치의 `deviceId` 가 끊긴다** — 그 순간 모든 기기가 새 기기가 되고
 * 시청 이력이 통째로 남남이 된다. 이름의 `fast.` 는 개명(v8) 전의 흔적이지만 **그 이유로
 * 고치지 않는다.**
 *
 * **v17 이 이것을 export 한다.** 앱이 로그인 요청에 실어 보내는 `deviceId` 가 **이 값과
 * 같아야** 서버가 기기 축 이력을 계정으로 승격할 수 있는데(`docs/38` §4.4), 앱에는
 * **다른 이름의 기기 ID 가 하나 더 있다**(TV2M SDK 의 `tv2m.deviceId` — 가구 그래프와
 * 발송 대상의 축이다). 두 값은 서로 다르고 **어느 것도 상대를 알지 못한다.**
 * 틀린 쪽을 실어 보내면 **승격이 조용히 무동작**이 된다 — 로그인은 성공하고 이력만
 * 올라오지 않는다.
 */
export const RECEIVER_DEVICE_ID_KEY = 'fast.deviceId';
/**
 * 마지막 브레이크가 끝난 시각(ms). v21.
 *
 * ★ **채널을 가리지 않는다.** `WatchScreen` 이 `key={channelId}` 라 채널을 바꾸면 리시버
 * 인스턴스가 통째로 새로 서는데, 이 값이 메모리에만 있으면 **채널 서핑이 `minGapSec` 을
 * 통째로 우회한다** — 리모컨으로 몇 번 훑으면 광고만 본다 (`docs/45` §3.4).
 *
 * `RECEIVER_DEVICE_ID_KEY` 와 같은 층이다. **저장소가 없으면 메모리로 떨어지고, 그
 * 사실이 `debugSnapshot()` 에 드러난다** — 조용히 우회하지 않는다.
 */
export const RECEIVER_LAST_AD_BREAK_KEY = 'fast.lastAdBreakEndedAt';
/**
 * 재보충의 답이 왔을 때 지금 광고가 마지막이면, 남은 시간이 이보다 짧으면 붙이지 않는다. 대기 버퍼에 복귀가 이미 실려
 * 꼬리 프리롤(0.15초)이 돌 수 있고, 그 버퍼를 새 광고로 갈아 끼우면 스왑이 덜 실린 버퍼를 올린다 — 무장이 다시 서고
 * 첫 프레임이 합성될 몫을 둔다.
 */
const REFILL_REARM_LEAD_SEC = 3;
/** 2026-10-29 · 회차의 끝이 이만큼 안이면 시작 전 광고의 컷을 다음 회차로 미룬다(초 · 꼬리에서는 컷의 앵커를 못 구한다) */
const LIVE_BEFORE_ITEM_TAIL_SEC = 4;
/**
 * 리시버 본체. 목록·순서·스킵 판정과 이벤트 전송을 담당한다.
 *
 * **프레임 단위 타이밍은 여기 없다.** 꼬리 프리롤과 마지막 프레임 스왑은 어댑터가 소유하고
 * (`armTailSwap`), 코어는 그 결과 콜백만 받는다. 근거는 `adapters/interfaces.ts` 머리말.
 * 이 분리 때문에 아래 상태 머신 전체가 목 어댑터로 검증된다.
 */
export class BloomReceiver {
    player;
    playlist;
    events;
    prerollSec;
    deviceId;
    state = 'idle';
    items = [];
    index = -1;
    elapsedSec = 0;
    /**
     * v35. **시청자가 멈췄는가** (절대 규칙 90·92). `ReceiverState` 에 `'paused'` 를 더하지 않는다 —
     * `state === 'playing'` 을 묻는 자리가 열이 넘고, 셋째 값이 그 판정을 조용히 뒤집는다.
     * 푸는 자리는 `setState` 하나다(전환은 새 것을 튼다).
     */
    paused = false;
    /**
     * 마지막 항목을 재생하는 동안 미리 받아 둔 다음 회차 목록.
     * 목록 끝에서 무갭을 유지하려면 **마지막 항목이 끝나기 전에** 다음 회차의 첫 항목 URL 을
     * 알아야 한다. 스왑 직후에 재요청하면 그 지점만 프리롤 없이 넘어간다.
     */
    pendingNextRound = null;
    /** `stop()`·`selectItem()` 이후 도착하는 어댑터 콜백을 무효화한다. */
    gen = 0;
    // ── v21. 광고 상태 넷 ──────────────────────────────────────────────────
    ads;
    storage;
    /** 지금 재생 중인 브레이크. `null` 이면 광고 중이 아니다 */
    adBreak = null;
    /**
     * 컷을 기다리는 브레이크. **`openBreak` 이 채우고 `afterSwap` 이 소비한다.**
     * 여기 있는 동안에는 아직 콘텐츠가 재생 중이다.
     */
    pendingAd = null;
    /** 이 회차에서 이미 발화한 큐 포인트. **회차가 바뀌면 비운다** */
    firedCuePoints = new Set();
    /**
     * v35. **응답을 기다리는 미드롤의 큐 포인트.** 발화 표시(`firedCuePoints`)는 요청을 쏠 때
     * 찍히고 `pendingAd` 는 응답이 와야 서므로, 그 사이에는 컷이 어디에도 없다 — 이 칸이 없으면
     * 왕복 동안 「10초 후」가 컷을 넘고, 늦게 온 광고가 **이미 지난 자리**를 자른다(`docs/71` §2.1 ·
     * 2026-10-01 실기가 찾았다). 응답이 오면(광고든 no fill 이든) 비운다.
     */
    midrollInFlight = null;
    /** 마지막 브레이크가 끝난 시각(ms). **저장소에서 읽고 끝날 때마다 쓴다** */
    lastBreakEndedAtMs = null;
    /** 2026-10-29 · 마지막 라이브 앞뒤 광고가 끝난 때(서버 시계 · `liveAdGapSec` 의 기준 · `recordedBreakQuiet`) */
    lastLiveAdEndedAtMs = null;
    /** 저장소가 없어 메모리로 떨어졌는가. `debugSnapshot()` 이 이것을 드러낸다 */
    adGapInMemoryOnly = false;
    /**
     * Ad Request 의 캐시 버스터. **단조 증가 정수다** — `buildTagUrl` 을 순수 함수로 두려면
     * 코어가 이 값을 채워야 하고(`AdRequest.correlator`), 그래야 「매번 다르다」를 단위
     * 테스트가 못박을 수 있다.
     */
    correlator = 0;
    // ── v23. 상품 오버레이 ───────────────────────────────────────────────────
    /**
     * v23 의 상태 여섯과 판정 — **v36 에 `OfferCues` 로 꺼냈다**(동작 그대로 · `offer-cues.ts` 머리).
     * Flower 엔진(`FlowerReceiver`)이 같은 클래스를 부른다. 리시버는 회차가 바뀌는 자리에서
     * `reset`·`arm` 을, `timeupdate` 축에서 `tick` 을, 브레이크·탐색에서 `close` 를 부른다.
     */
    offerCards;
    // ── v33. 라이브 바이패스 상태 넷 ──────────────────────────────────────
    /**
     * 앞으로 남은 이벤트 창들. **플레이리스트에서 온다** (절대 규칙 83).
     *
     * 배열인 것이 요점이다 — 하나만 실으면 이벤트 사이마다 목록을 다시 받아야 하고, 그
     * 재요청이 곧 `resync()` 이며 그것은 **복귀 지점을 「서버가 아는 것」 하나로 줄인다**
     * (비로그인에게는 아무것도 남지 않는다 · `docs/67` D23).
     */
    liveWindows = [];
    /** **바이패스 중인가.** `null` 이면 목록 재생 모드다 */
    live = null;
    /**
     * ★ **시청자가 이번 창에서 스스로 벗어난 이벤트들** (`docs/67` §9.3).
     *
     * 라이브 중에 편성표에서 녹화 회차를 고르면 바이패스를 나온다. 그때 이 집합에 넣지
     * 않으면 `maybeEnterLive()` 가 **다음 틱(250ms)에 도로 끌고 들어가고**, 시청자에게는
     * 「눌렀는데 아무 일도 안 일어난다」다. 채널을 다시 들어오거나 편성표의 라이브 상자를
     * 누르면 그때 다시 합류한다(`stop()` 이 비운다).
     */
    skippedEventIds = new Set();
    /**
     * ★ v33-c/d. **이번 창의 컷이 어디까지 왔는가** — 준비 중(`preparing`)이거나 무장함(`armed`).
     *
     * ① **한 창에 한 번만 무장한다** (2026-09-22 · `docs/68` §2.1). `maybeEnterLive()` 는
     *    `onTimeUpdate` 축(약 4Hz)에서 돈다. 표시가 없으면 **250ms 마다 다시 무장**하고,
     *    그때마다 꼬리가 새것으로 갈려 **`prerolled` 가 거짓으로 되돌아간다** — 스왑이 한 번도
     *    안 터져 즉시형이 받아 가고, 복귀 지점이 **청크 경계가 아니라 그 순간의 경과**가 된다
     *    (실측: 컷은 56 으로 옳게 계산돼 있었는데 보고된 값은 51.17 이었다). 광고의 예약형에는
     *    `pendingAd` 가 그 자리를 하고 있었다 — **라이브에만 없었다.**
     *
     * ② **적재가 끝나기 전에 컷을 무장하지 않는다** (2026-09-23 · `docs/68` §2.0-e). 전에는 창
     *    20초 전에 적재와 무장을 함께 했는데, 창 전의 스트림 주소는 **503** 이라(`docs/67`
     *    §5.2) 적재가 **언제나** 실패했고, 꼬리 스왑은 그것을 모른 채 **빈 버퍼를 화면에
     *    올렸다** — 검은 화면에 🔴 LIVE 배지. 그 버퍼는 `timeupdate` 를 내지 않으므로
     *    `maybeLeaveLive()` 도 영영 안 돌아 **창이 끝나도 돌아오지 않았다.** 녹화물을 보다가
     *    창을 맞는 길, 즉 **데모의 본 장면**이다.
     */
    liveCut = null;
    /** 적재 실패 뒤 이 시각(서버 시계 ms)까지는 다시 시도하지 않는다 */
    liveCutRetryAtMs = 0;
    // ── 2026-10-29 · 라이브 이벤트 앞뒤 광고 (`docs/45` §18) ──────────────────
    /** 시작 전 광고를 이미 물은 이벤트 — **이벤트마다 한 번**(no fill 도 물은 것이다) */
    liveBeforeAsked = new Set();
    /** 시작 전 광고를 묻는 중이다 — 그동안 라이브 진입은 기다린다(개시 큐 이벤트) */
    liveBeforeInFlight = false;
    /** 받아 두고 컷을 기다리는 시작 전 광고 */
    liveBeforePod = null;
    /** 종료 후 광고를 이미 물은 이벤트 */
    liveAfterAsked = new Set();
    /** 나가 있는 종료 후 광고 요청 — 창이 닫히는 순간 그 답을 기다린다 */
    liveAfterRequest = null;
    /** 받아 둔 종료 후 광고 */
    liveAfterPod = null;
    listeners = new Map();
    constructor(opts) {
        this.player = opts.player;
        this.prerollSec = opts.prerollSec ?? DEFAULT_PREROLL_SEC;
        this.ads = opts.ads ?? null;
        this.offerCards = new OfferCues(opts.offers ?? null, {
            policy: () => this.offerPolicy,
            elapsedSec: () => this.elapsedSec,
            isPlaying: () => this.state === 'playing',
            inAdBreak: () => this.adBreak !== null,
            currentItem: () => this.getCurrentItem(),
            requestContext: () => ({
                channelId: this.playlist.servedChannelId ?? '',
                lang: this.adPolicy?.language ?? 'ko',
                deviceId: this.deviceId,
                playlistId: this.playlist.playlistId ?? '',
            }),
            nextCorrelator: () => (this.correlator += 1),
            correlator: () => this.correlator,
            emit: (view) => {
                this.emit('offerChanged', view);
            },
        });
        this.storage = opts.storage ?? null;
        this.deviceId = resolveDeviceId(opts.deviceId, opts.storage);
        this.lastBreakEndedAtMs = readLastBreakEndedAt(opts.storage);
        this.adGapInMemoryOnly = opts.storage === undefined;
        this.playlist = new PlaylistClient(opts.network, opts.serverUrl, opts.channelId, this.deviceId);
        this.events = new EventQueue(opts.network, opts.serverUrl);
        this.player.onTimeUpdate((sec) => {
            this.elapsedSec = sec;
            // v21. **예약형 둘이 이 축에서 열린다** (`docs/45` §8.4 의 ★). `resolveNextUrl()`
            // 자리에 걸면 그것이 `armNext()` 의 첫 줄이라 **회차 시작 직후**에 불려,
            // 42분 회차의 광고 요청이 컷보다 42분 앞에 나간다.
            this.maybeOpenScheduledBreak();
            // v23. **셋이 이 축에서 돈다** — 요청(T−10초) · 표시(T) · 걷기(T+displaySec).
            // 광고와 **같은 시계**이고, 그것이 Post 8 의 「SDK 는 콘텐츠 내내 단말에 있다」다.
            this.offerCards.tick();
            /*
             * ★ v33. **바이패스를 여닫는 자리 둘도 이 축이다** (`docs/67` §7.1 · 절대 규칙 83).
             *
             * **타이머를 만들지 않는다.** `tsconfig.core.json` 이 `types: []` 라 `setTimeout` 이
             * 애초에 없고(절대 규칙 6), **그것이 여기서는 이득이다** — 타이머로 하면 탭이 숨은
             * 동안 크롬이 그것을 1분까지 늦춰 「돌아왔더니 라이브가 5분 늦게 시작했다」가 된다.
             * 프레임 축은 재생이 도는 동안만 돌고, **재생이 멈춰 있으면 바이패스도 필요 없다.**
             */
            // 2026-10-29 · 라이브 시작 전 광고 — **진입 판정 앞이다**(받아 둔 포드가 있으면 진입이 기다린다 · `docs/45` §18)
            this.maybeLiveBeforeBreak();
            this.maybeEnterLive();
            this.maybeLeaveLive();
        });
        this.player.onError((e) => {
            /*
             * ★ v21. **광고 중의 재생 오류는 화면을 덮지 않는다** (`docs/45` §8.7).
             *
             * 그 표의 넷째 줄이 「광고 크리에이티브가 재생 불가 → 그 광고를 건너뛰고, pod 이
             * 비면 복귀」이고, 머리글이 **「오류는 조용하다 — 광고는 재생을 막지 않는다」**다.
             * 무조건 `fail()` 하면 `state === 'error'` 가 되어 앱이 **오류 스크림을 덮고**,
             * 시청자에게는 **광고 하나 못 튼 것이 채널이 고장 난 것**으로 보인다
             * (절대 규칙 51 이 약속한 「광고도 재생을 멈추지 않는다」가 거기서 깨진다).
             *
             * **되돌아가는 자리는 이미 있다** — `recoverByLoad` 의 광고 분기가 복귀 지점으로
             * 적재한다(§14 의 7). 그것이 실패하면 `closeAdBreak` 이 그때 `fail()` 하므로,
             * **콘텐츠가 진짜로 깨진 경우는 여전히 화면에 뜬다.**
             *
             * `emit('error')` 는 그대로 낸다 — 화면은 `state` 로만 판단하므로 덮이지 않고
             * (`useReceiver` 의 그 분기), 기록에는 남아야 한다. **조용한 것은 화면이지
             * 계측이 아니다.**
             *
             * 2026-08-24 아이폰 실기: pod 의 뒷 광고가 준비되지 않아 `PLAYBACK_FAILED` 가
             * 화면을 덮었다. 그 원인(어댑터의 지연 정리)은 따로 고쳤고, **이 줄은 같은 일이
             * 다시 나도 채널이 멈추지 않게 한다.**
             */
            if (this.adBreak !== null) {
                this.emit('error', {
                    code: 'PLAYBACK_FAILED',
                    message: '광고를 재생하지 못해 콘텐츠로 돌아간다.',
                    cause: e,
                });
                void this.recoverByLoad(this.gen);
                return;
            }
            this.fail({ code: 'PLAYBACK_FAILED', message: '재생 중 오류가 발생했다.', cause: e });
        });
        this.player.onTransition((m) => {
            this.emit('transition', m);
        });
    }
    // ── 공개 API ────────────────────────────────────────────────────────────
    /**
     * @param opts.preRoll 프리롤을 열 것인가. **기본은 연다.** `resync()` 가 `false` 로
     *   부르는 것이 요점이다 — 탭을 나갔다 돌아올 때마다 광고가 나오면 안 된다
     *   (`docs/45` §14 의 4). 그 실수는 **15초 문턱을 넘을 때만** 돌아 재현이 들쭉날쭉하다.
     *
     * @param opts.from **기기가 기억한 시작 항목** (v26, 절대 규칙 70). 서버가 목록을 그
     *   항목으로 회전시켜 준다. **홈쇼핑 채널에서만 준다.**
     *
     *   ── 이 인자가 `selectItem()` 이 아닌 이유 ──────────────────
     *
     *   `resync()` 가 「왜 `selectItem()` 이 아닌가」에 적어 둔 셋 중 **둘이 여기에도 그대로
     *   걸린다.** ① 시청자가 고른 것이 아니므로 **`select` 이벤트를 내지 않는다** — 부팅마다
     *   한 건씩 나가면 절대 규칙 33 이 지키려는 그 지표가 매 실행 오염된다. ② 전환 스왑이
     *   아니라 **적재**다.
     *
     *   ★ **일반 채널에 주지 말 것.** 서버의 `resolveResume` 이 이 값을 「고른 것」으로 보아
     *   `positionSec: 0` 을 내고, 화면의 첫 주기 보고가 그 0 으로 **저장된 이어보기 지점을
     *   덮는다 — 되돌릴 방법이 없다.** 홈쇼핑에서는 `resolveResume` 이 첫 줄에서 `null` 을
     *   내므로(`kind !== 'general'`) **회전만 남는다.** 막는 자물쇠 둘은 앱에 있다
     *   (`last-items.ts` 의 `entryStartFor` 와 쓰기 자리).
     *
     *   ★★ **`resync()` 는 이 값을 주지 않는다.** 지금 복귀 재동기화는 일반 채널에서만
     *   돌아(`WatchScreen` 의 `isGeneral` 가드) 홈쇼핑에 닿지 않는다. **훗날 홈쇼핑에 그것을
     *   켜는 날 여기가 첫 자리다** — 그대로 두면 돌아올 때마다 편성 첫 상품으로 튄다.
     *   그때 줄 값은 「기억한 것」이 아니라 **「지금 재생 중인 것」**이고, 그 판정은 화면이
     *   갖는다.
     */
    async start(opts = {}) {
        const gen = ++this.gen;
        /*
         * v21. **광고 상태를 먼저 비운다.** `resync()` 가 이 경로로 오는데, **광고 중에 탭을
         * 나갔다 돌아오면** 상태가 남아 `isInAdBreak()` 가 영원히 참이 되고 — 화면은 콘텐츠를
         * 트는데 광고 UI 가 걷히지 않는다. `stop()` 과 같은 이유의 초기화다.
         *
         * ★ **살아 있던 브레이크는 「끝났다」로 남긴다** (v21/F13, 실기가 드러냈다).
         * 전에는 `closeAdBreak` 한 곳에서만 기록해서, **광고 중에** 나갔다 돌아오면 그
         * 브레이크가 없었던 일이 되고 `minGapSec` 이 그 자리를 지키지 못했다.
         */
        if (this.adBreak !== null) {
            this.markBreakEnded();
            this.emit('adBreakChanged', null);
        }
        this.adBreak = null;
        this.pendingAd = null;
        this.firedCuePoints.clear();
        this.midrollInFlight = null;
        // v23. **`resync()` 가 이 경로로 온다** — 상태를 비우지 않으면 돌아온 뒤에도 옛 카드가
        // 떠 있고, 그 카드는 이미 지난 장면의 것이다 (절대 규칙 64).
        this.offerCards.reset();
        this.setState('loading');
        try {
            // v26. **인자가 없으면 지금까지와 한 바이트도 다르지 않다** — 서버가 이어보기를
            // 실어 준다. `from` 이 있을 때만 `startFromProductId` 가 붙는다 (절대 규칙 70).
            await this.playlist.fetch(opts.from);
        }
        catch (e) {
            this.fail({
                code: 'PLAYLIST_FETCH_FAILED',
                message: '플레이리스트를 받지 못했다.',
                cause: e,
            });
            return;
        }
        if (gen !== this.gen)
            return;
        /*
         * ★ v33. **창을 먼저 싣는다** (절대 규칙 83). 판정은 리시버가 하고 서버는 창만 준다.
         *
         * ★★ **같은 이벤트가 계속 도는 동안 `returnTo` 를 지킨다** (D26). `resync()` 도
         * 채널 재마운트도 이 경로로 오는데, 비우면 **비로그인 시청자의 복귀 지점이 사라진다**
         * (계정이 있으면 진입 순간의 진행 보고가 서버에 남아 있다 · `docs/67` §7.3).
         */
        this.liveWindows = this.playlist.live;
        const keptLive = this.live;
        this.items = this.playlist.items;
        if (this.items.length === 0) {
            // 404 가 아니라 정상 상태다 — `ready` 인 상품이 아직 없을 뿐이다.
            this.setState('idle');
            this.emit('error', { code: 'EMPTY_PLAYLIST', message: '재생할 상품이 없다.' });
            return;
        }
        this.emit('playlistLoaded', this.items);
        this.index = 0;
        const first = this.items[0];
        if (!first)
            return;
        /*
         * v21. **프리롤은 즉시형이다** — 재생 전에 최대 3초를 기다린다(`docs/45` §8.6).
         * 기다리는 동안 `state === 'loading'` 이라 **앱이 이미 스피너를 그린다.**
         *
         * 열리면 광고를 먼저 적재하고, `resumeAtSec` 은 `null` 이다 — 복귀는 「첫 회차의
         * 이어보기 지점」이지 「잘린 자리」가 아니다.
         */
        /*
         * ★★★ v33. **지금이 창 안이면 바이패스로 곧바로 간다** (`docs/67` §7.2 · D8).
         *
         * 프리롤을 열지 않는다 — 라이브에 광고를 붙이지 않는 것이 이번 판의 범위이고(§16),
         * 무엇보다 **창은 이미 흐르고 있다**(광고를 트는 동안 그만큼 놓친다).
         */
        const { current } = liveWindowAt(this.liveWindows, this.liveNow());
        if (current !== null && !this.skippedEventIds.has(current.eventId)) {
            /*
             * **회차를 먼저 알린다** — F13 이 프리롤에서 값을 치른 그 순서다. 안 알리면 앱의
             * `view.item` 이 `null` 인 채라 컨트롤 바가 통째로 안 그려지고, **영상만 나오므로
             * 「라이브가 잘 나온다」로 보인다**(`docs/67` D24).
             */
            this.emit('itemChanged', first, 0);
            // 같은 이벤트가 계속 도는 중이면 두고 온 자리를 되살린다(위 ★★).
            if (keptLive !== null && keptLive.window.eventId === current.eventId) {
                this.live = { ...keptLive, window: current, returnArmed: false };
            }
            await this.enterLiveNow(gen, current);
            return;
        }
        const preRollOpened = (opts.preRoll ?? true)
            ? await this.openBreak('preroll', null, true, { type: 'startCurrent' })
            : false;
        if (gen !== this.gen)
            return;
        try {
            if (preRollOpened && this.pendingAd) {
                await this.startPendingBreakNow(gen, first);
                return;
            }
            await this.player.load(first.videoUrl, this.resumeSecFor(first.productId));
            await this.player.play();
        }
        catch (e) {
            /*
             * ★ **앞지름당한 조작의 실패는 화면을 덮지 않는다** (2026-08-27 실기).
             *
             * 세대 가드가 지금까지 **성공 경로에만** 있었다. 그런데 여기의 `await` 는 어댑터의
             * `arm()` 을 기다리고 그것은 최대 `ARM_TIMEOUT_MS`(15초)다 — 그 사이 시청자가 다른
             * 조작을 하면(`selectItem()` 이 `++this.gen` 을 한다) **이 실패는 이미 버려진
             * 조작의 것**인데, 그대로 `fail()` 하면 **시청자가 방금 고른 회차가 멀쩡히 재생
             * 중인 화면을 오류 스크림이 덮는다.**
             *
             * 실기에서 본 모양 — 「다음 회차」를 누르고 6초를 기다리다 목록에서 다른 회차를
             * 골랐다. 고른 것은 즉시 재생됐고, **누른 지 15초 뒤에** 「건너뛰기 전환이
             * 실패했다」가 화면을 덮었다.
             *
             * **증상이 조용한 이유가 여기 있다** — 실패한 조작은 시청자가 이미 버린 것이라
             * 화면에 예고가 없고, 경합이라 같은 순서를 다시 밟아도 안 나기도 한다. 자연 재생만
             * 하는 하네스는 원리적으로 못 본다.
             *
             * **여섯 자리가 전부 같은 모양이었다** — 아래 다섯은 이 주석을 가리킨다.
             */
            if (gen !== this.gen)
                return;
            this.fail({ code: 'PLAYBACK_FAILED', message: '재생을 시작할 수 없다.', cause: e });
            return;
        }
        if (gen !== this.gen)
            return;
        /*
         * ★★ **튼 자리로 시작한다. 0 이 아니다** (v27/TG18, 2026-09-04).
         *
         * 위 `player.load` 에 이어보기 지점을 넘겨 놓고 여기서 0 으로 되돌리면, **첫
         * `timeupdate` 가 올 때까지 `getElapsedSec()` 이 0 을 말한다.** 실측에서
         * `<video>.currentTime` 이 이미 **242.1** 인데 이 값은 **0** 이었다.
         *
         * 그 창을 보는 것이 편성표다 — 채널을 오갔다 돌아온 직후에 편성표를 열면
         * `shouldReanchor` 가 그 0 을 참으로 믿어 **행을 회차 맨 앞에 그리고**, 다음 초에
         * 참값으로 다시 잡으며 **한 번 크게 튄다**(242초 = 218px). 무대에서 「시점이
         * 출렁인다」로 보이는 그 모양이다.
         *
         * **광고 복귀 경로는 이미 이렇게 하고 있었다** — `closeAdBreak` 의
         * `this.elapsedSec = target.at ?? 0`. `start()` 만 따라오지 않았다.
         *
         * 창이 짧아 노트북에서는 표본 하나로만 잡힌다. **네트워크가 느릴수록 길어진다.**
         */
        this.elapsedSec = this.resumeSecFor(first.productId) ?? 0;
        this.setState('playing');
        // v23. **회차가 정해진 뒤에 큐를 무장한다.** 이어보기 지점을 함께 넘기는 것이 요점이다 —
        // 지나간 큐가 **몰려 뜨는 것**을 막는 유일한 자리다 (`OfferCues.arm` 의 ★).
        //
        // ★ **여섯 자리 중 이어보기 지점을 주는 것은 여기뿐이다.** 위 `player.load` 에 넘긴
        // 그 값과 **같은 식이어야 한다** — 갈리면 「튼 자리」와 「무장한 자리」가 어긋난다.
        this.offerCards.arm(first, this.resumeSecFor(first.productId) ?? 0);
        this.emit('itemChanged', first, 0);
        this.send('start', first);
        void this.armNext(gen);
    }
    /**
     * 서버의 이어보기 지점으로 다시 맞춘다 (v18).
     *
     * **`start()` 를 다시 부르는 것과 같되 꼬리 스왑을 먼저 끊는다.** 그 한 줄이 이 메서드가
     * 따로 있는 이유 전부다 — `armTailSwap` 은 **어댑터가 물리적으로 `<video>` 를 바꾸는**
     * 예약이고, 코어의 `gen` 검사는 그 뒤에 오는 **콜백만** 막는다. 끊지 않으면 재동기화로
     * 새 회차를 적재한 직후에 옛 예약이 화면을 다음 회차로 바꿔 버린다.
     * `stop()` 과 `selectItem()` 이 같은 이유로 먼저 부른다.
     *
     * ── 왜 `selectItem()` 이 아닌가 ────────────────────────────
     *
     * 셋이 다르다. ① `startFromProductId` 를 주지 않으므로 서버가 **이어볼 회차와 그 초**를
     * 그대로 실어 준다(선택으로 부르면 v18 규칙 ①에 따라 **언제나 0초**가 되어 이어보기가
     * 사라진다). ② `select` 이벤트를 내지 않는다 — 시청자가 고른 것이 아니다(절대 규칙 33 이
     * 지키려는 것은 「고른 것」의 기록이고, 여기에 그 값을 섞으면 선택 지표가 오염된다).
     * ③ 전환 스왑이 아니라 적재다 — 새 전환 지표를 만들지 않으려는 것이고, 돌아온 시청자에게
     * 적재 한 번은 채널 전환과 같은 경험이다.
     *
     * **판정은 호출자가 한다.** 이 메서드는 「맞춰라」이지 「필요하면 맞춰라」가 아니다 —
     * 무엇을 근거로 움직일지는 화면의 순수 함수(`resume-return.ts`)에 있고, 거기에 두어야
     * node 환경 테스트가 그것을 본다.
     */
    async resync() {
        this.player.cancelTailSwap();
        /*
         * v21. **프리롤을 열지 않는다** (`docs/45` §3.2 의 규칙 ② · §10.2).
         * 복귀 재동기화는 **시청자의 조작이 아니다** — 열면 탭을 나갔다 돌아올 때마다 광고가
         * 나오고, 15초 문턱을 넘을 때만 돌아서 **재현이 들쭉날쭉하다.**
         */
        return this.start({ preRoll: false });
    }
    stop() {
        this.gen += 1;
        this.player.cancelTailSwap();
        this.player.stop();
        this.items = [];
        this.index = -1;
        this.pendingNextRound = null;
        /*
         * v21. **광고 상태 넷 중 셋을 비운다.** v19 가 `swapping = false` 를 여기 넣어야 했던
         * 것과 같은 종류의 누락을 막는다 — 남기면 다음 채널의 첫 스왑이 「광고 중」으로 판정돼
         * 인덱스가 오르지 않는다.
         *
         * ★ **`lastBreakEndedAtMs` 는 남긴다.** 그것이 `minGapSec` 이 채널을 가로질러 사는
         * 유일한 근거이고, 여기서 비우면 **채널 서핑이 최소 간격을 우회한다.**
         *
         * ★★ **그리고 광고 중에 떠났으면 그 브레이크를 「끝났다」로 기록한다** (v21/F13).
         * 전에는 `closeAdBreak` 한 곳에서만 기록했다 — 즉 **끝까지 본 광고만** 셈에 들었고,
         * 광고 도중에 채널을 바꾸면 그 브레이크가 **없었던 일**이 됐다. 그러면 다음 채널이
         * 곧바로 또 프리롤을 열고, 그것을 반복하면 **광고만 보게 된다** — 이 주석 바로 위가
         * 막으려던 그 일이 다른 문으로 들어온 것이다.
         *
         * **하네스가 못 봤다** — `ad.minGapSurvivesChannelSwitch` 는 pod 이 **끝나기를
         * 기다린 뒤에**(`waitForContent`) 채널을 바꾼다. 무대에서는 광고 중에 바꿨고,
         * 기록에 **같은 채널 프리롤이 24초 안에 셋**으로 남았다(`docs/46` §3.11).
         */
        const hadBreak = this.adBreak !== null;
        this.adBreak = null;
        this.pendingAd = null;
        this.firedCuePoints.clear();
        this.midrollInFlight = null;
        /*
         * ★ v33. **바이패스 상태를 비운다** (`docs/67` §14-16). 남기면 다음 채널이 라이브
         * 상태로 서서 **버튼 여섯이 없는 화면**이 되고, 그 채널에는 이벤트가 없으므로
         * 아무도 그것을 걷어 주지 않는다.
         *
         * **`skippedEventIds` 도 함께 비운다** — 채널을 다시 들어오는 것이 「다시 합류하겠다」의
         * 뜻이다(`docs/67` §9.3).
         */
        this.closeLive();
        this.liveWindows = [];
        this.skippedEventIds.clear();
        this.liveCut = null;
        this.liveCutRetryAtMs = 0;
        // 2026-10-29 · 라이브 앞뒤 광고 — 채널을 다시 들어오면 다시 묻는다
        this.liveBeforeAsked.clear();
        this.liveBeforeInFlight = false;
        this.liveBeforePod = null;
        this.liveAfterAsked.clear();
        this.liveAfterRequest = null;
        this.liveAfterPod = null;
        // v23. **상품 상태 여섯을 전부 비운다.** `lastBreakEndedAtMs` 와 달리 **남길 이유가
        // 없다** — 최소 간격이 채널을 가로질러 살아야 하는 이유(서핑 우회)가 상품에는 없다.
        this.offerCards.reset();
        if (hadBreak) {
            this.markBreakEnded();
            this.emit('adBreakChanged', null);
        }
        this.setState('idle');
        void this.events.flush();
    }
    /**
     * 특허 ③. `canSkip()` 이 false 면 조용히 무시한다 — 에러가 아니다.
     *
     * **v21 이 `Promise<void>` 로 바꿨다.** 즉시형 광고 요청이 최대 3초를 기다리기
     * 때문이고, 기다리는 동안 `state === 'switching'` 이라 **앱이 이미 그 상태를 그린다** —
     * 새 UI 가 없다.
     *
     * ★ **광고 중에는 회차를 건너뛸 수 없다.** 광고 스킵은 `skipAd()` 라는 **다른
     * 메서드**다 — 하나로 겸하면 상태 가드가 한쪽을 조용한 무동작으로 만들고,
     * `send('skip')` 이 **회차 이탈률을 광고 스킵으로 부풀린다** (`docs/45` §8.5).
     */
    async skip() {
        if (this.adBreak !== null)
            return;
        // v33. **라이브는 건너뛸 수 없다** (절대 규칙 85). 화면에 버튼이 없지만
        // (`docs/67` §8.2) 리모컨·하네스가 부를 수 있어 여기서도 막는다 — 막지 않으면
        // `send('skip')` 이 나가 **회차 이탈률이 부풀려진다**(`skipAd` 를 가른 그 근거).
        if (this.live !== null)
            return;
        if (this.state !== 'playing')
            return;
        const current = this.getCurrentItem();
        if (!canSkip(current, this.elapsedSec))
            return;
        const gen = this.gen;
        this.send('skip', current);
        this.setState('switching');
        // v21. **`swapNow` 앞에 무장한다** — 조작 정책이 가리키는 브레이크를 연다.
        // `none` 이면 아무 일도 없고 지금까지와 한 프레임도 다르지 않다.
        const kind = this.transitionKind('skip');
        if (kind !== null)
            await this.openBreak(kind, null, true, { type: 'advance' });
        if (gen !== this.gen)
            return;
        try {
            await this.player.swapNow('skip');
        }
        catch (e) {
            // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
            if (gen !== this.gen)
                return;
            this.fail({ code: 'PLAYBACK_FAILED', message: '건너뛰기 전환이 실패했다.', cause: e });
            return;
        }
        if (gen !== this.gen)
            return;
        this.afterSwap('skip', null);
    }
    /**
     * 광고를 건너뛴다. **`skip()` 과 다른 메서드다** (`docs/45` §8.5).
     *
     * 나누는 이유가 둘이다. ① `skip()` 의 첫 줄이 `state !== 'playing'` 이라 광고 중 상태를
     * 어떻게 두든 한쪽이 조용한 무동작이 된다. ② `skip()` 은 `send('skip')` 으로 **`Event`
     * 에 회차 스킵을 기록하는데**, 광고 스킵이 거기 섞이면 **회차 이탈률이 광고 스킵으로
     * 부풀려지고** 그 오염은 **분석에서만** 드러난다.
     */
    async skipAd() {
        if (!this.canSkipAd())
            return;
        const breakState = this.adBreak;
        const ad = breakState?.items[breakState.index];
        if (!breakState || !ad || breakState.skipping)
            return;
        const gen = this.gen;
        const skippedIndex = breakState.index;
        const wasLast = skippedIndex === breakState.items.length - 1;
        // **광고 이벤트는 `Event` 로 가지 않는다** (절대 규칙 55) — VAST 트래킹으로 간다.
        this.ads?.track(ad.tracking.skip);
        /*
         * ★ **스킵 재보충** (2026-10-28 · `docs/45` §17). 건너뛴 광고의 남은 몫만큼 브레이크가 비었다 — 그 시간을 광고 서버에
         * 다시 묻고(`_ext=1` · 이 브레이크의 소재를 뺀다) 받은 광고를 **브레이크 끝에** 붙인다. 못 채운 자투리는 필러로 메우지
         * 않는다 — 마지막 광고가 끝나면 그대로 회차로 간다.
         *
         * - 뒤에 광고가 남아 있으면 대기 버퍼에 이미 다음 광고가 있다 — **곧바로 넘기고** 재보충은 그 뒤에서 묻는다.
         * - 마지막 광고였으면 대기 버퍼에는 복귀가 있다 — **답을 먼저 듣고**(최대 `AD_REFILL_TIMEOUT_MS`) 광고가 왔으면 그것을
         *   대기 버퍼에 올린 뒤 넘긴다. 기다리는 동안 건너뛴 광고가 계속 나간다.
         */
        breakState.freedSec += Math.max(0, ad.durationSec - this.elapsedSec);
        breakState.skipping = true;
        let playedSec;
        /*
         * ★ **브레이크 시계는 건너뛴 몫을 세지 않는다**(2026-10-28 · `docs/45` §17.3). 건너뛴 광고는 실제로 나간 만큼만
         * `playedBeforeSec` 에 든다(`advanceAd` 의 인자) — 남은 시간이 스킵에서 뛰지 않는다. v21/F13 은 반대로 「지나온 광고의
         * 길이 합」으로 세어 스킵하면 남은 시간이 건너뛴 몫만큼 줄었고(그때의 실측 결함은 그 몫을 두 번 센 것이었다), 재보충이
         * 생긴 뒤 「확 줄었다가 다시 는다」가 설명하기 어려워 바꿨다(사용자 결정).
         */
        try {
            if (wasLast) {
                await this.refillBreak(gen, breakState);
                // 기다리는 사이 건너뛴 광고가 스스로 끝났으면(꼬리 스왑) 이미 다음으로 갔다 — 두 번 넘기지 않는다
                if (gen !== this.gen || this.adBreak !== breakState || breakState.index !== skippedIndex)
                    return;
                const next = breakState.items[skippedIndex + 1];
                // 실패는 삼킨다 — `swapNow` 가 같은 주소를 다시 적재하고, 그것도 안 되면 아래 `fail` 이다
                if (next)
                    await this.player.prepareNext(next.videoUrl).catch(() => undefined);
                if (gen !== this.gen || this.adBreak !== breakState || breakState.index !== skippedIndex)
                    return;
            }
            // 건너뛴 광고가 실제로 나간 만큼 — **넘기기 직전에** 잡는다(마지막 광고면 답을 기다린 몫까지 든다). 넘긴 뒤의 경과는
            // 새 버퍼의 보고가 섞일 수 있다 — Flower 엔진은 그 자리에서 345초를 읽었다(bloom.26 첫 빌드 · `docs/74` §4G.7)
            playedSec = Math.min(this.elapsedSec, ad.durationSec);
            await this.player.swapNow('skip');
        }
        catch (e) {
            // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
            if (gen !== this.gen)
                return;
            this.fail({ code: 'PLAYBACK_FAILED', message: '광고 건너뛰기가 실패했다.', cause: e });
            return;
        }
        finally {
            breakState.skipping = false;
        }
        if (gen !== this.gen)
            return;
        this.advanceAd(gen, playedSec);
        if (!wasLast)
            void this.refillBreak(gen, breakState);
    }
    /**
     * **스킵 재보충** (`docs/45` §17) — 비어 있는 시간(`freedSec`)을 광고 서버에 다시 묻고 받은 광고를 브레이크 끝에 붙인다.
     * 이미 나가 있는 재보충이 있으면 그 답을 먼저 기다린다(그 답이 빈 시간을 줄인다). 실패·no fill 은 조용하다(`openBreak` 와
     * 같다) — 브레이크가 그만큼 짧아질 뿐이다.
     */
    async refillBreak(gen, state) {
        while (state.refill !== null)
            await state.refill;
        if (gen !== this.gen || this.adBreak !== state)
            return;
        const run = this.requestRefill(gen, state);
        state.refill = run;
        try {
            await run;
        }
        finally {
            if (state.refill === run)
                state.refill = null;
        }
    }
    async requestRefill(gen, state) {
        const policy = this.adPolicy;
        const pdSec = Math.floor(state.freedSec);
        if (policy === null || this.ads === null || pdSec < 1)
            return;
        const current = this.getCurrentItem();
        this.correlator += 1;
        const items = await this.ads.requestAds({
            adServerUrl: policy.adServerUrl,
            channelId: this.playlist.servedChannelId ?? '',
            breakKind: state.kind,
            maxDurationSec: pdSec,
            productId: current?.productId ?? '',
            // 상관관계 키 — 광고 중의 `elapsedSec` 은 광고의 초다. 미드롤이면 돌아갈 자리를 싣는다
            positionSec: state.returnTo.type === 'resume' ? Math.floor(state.returnTo.atSec ?? 0) : 0,
            language: policy.language ?? 'ko',
            deviceId: this.deviceId,
            playlistId: this.playlist.playlistId ?? '',
            correlator: this.correlator,
            refill: true,
            excludeCreativeIds: state.items.map((ad) => ad.creativeId),
        }, { timeoutMs: AD_REFILL_TIMEOUT_MS });
        if (gen !== this.gen || this.adBreak !== state || items.length === 0)
            return;
        // 서버가 `pd` 를 지키지만 한 번 더 — 빈 시간을 넘는 광고는 붙이지 않는다(브레이크가 약속보다 길어진다)
        const accepted = [];
        let addedSec = 0;
        for (const ad of items) {
            if (addedSec + ad.durationSec > pdSec)
                continue;
            accepted.push(ad);
            addedSec += ad.durationSec;
        }
        if (accepted.length === 0)
            return;
        /*
         * 지금 광고가 마지막이고 스킵이 돌고 있지 않으면 **꼬리 무장이 복귀를 향해 있다** — 다시 무장해 새 광고로 돌린다.
         * 끝이 가까우면 붙이지 않는다(`REFILL_REARM_LEAD_SEC`). 스킵이 돌고 있으면 그 스킵이 대기 버퍼를 갈아 끼운다.
         */
        const rearm = state.index === state.items.length - 1 && !state.skipping;
        if (rearm) {
            const playing = state.items[state.index];
            if (!playing || playing.durationSec - this.elapsedSec < REFILL_REARM_LEAD_SEC)
                return;
        }
        state.items.push(...accepted);
        state.freedSec = Math.max(0, state.freedSec - addedSec);
        this.emit('adBreakChanged', this.getAdBreak());
        if (rearm)
            this.armAdNext(gen);
    }
    // ── v35. 녹화물 탐색·정지 (`docs/71` · 절대 규칙 90~92) ──────────────────
    /**
     * 정지·탐색을 받을 수 있는가 — **녹화물을 재생 중일 때만**이다.
     *
     * 광고 중(브레이크의 길이가 채널의 약속이다 · 규칙 53)과 라이브 바이패스 중(리시버는 그
     * 스트림 안을 모른다 · 규칙 85)에는 아니다. 화면은 이 값이 거짓이면 버튼을 **그리지 않는다**.
     */
    canControlPlayback() {
        return (this.state === 'playing' &&
            this.adBreak === null &&
            this.live === null &&
            this.getCurrentItem() !== null);
    }
    /** 시청자가 멈췄는가. **화면이 정지를 그리는 근거는 이것 하나다** (절대 규칙 90) */
    isPaused() {
        return this.paused;
    }
    /**
     * 시청자의 정지 (절대 규칙 92). **이벤트를 내지 않는다** — `EVENT_TYPES` 여섯 어디에도
     * 해당하지 않는다.
     *
     * 정지 중에는 미드롤 컷 · 상품 카드 · 라이브 진입이 **저절로 열리지 않는다** — 셋 다
     * `timeupdate`·프레임 축에서 돌고, 그 축이 멈추기 때문이다(`docs/71` §2.3).
     */
    pause() {
        if (this.paused || !this.canControlPlayback())
            return;
        this.player.pause();
        this.paused = true;
        this.emit('pausedChanged', true);
    }
    /**
     * 정지를 푼다. **거부되면 정지로 되돌린다** — 오류 화면이 아니다(재생은 멈춰 있을 뿐이고,
     * 버튼을 다시 누르면 된다). 기록에는 남긴다.
     */
    async resume() {
        if (!this.paused)
            return;
        const gen = this.gen;
        this.paused = false;
        this.emit('pausedChanged', false);
        try {
            await this.player.play();
        }
        catch (e) {
            // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
            if (gen !== this.gen || this.paused)
                return;
            this.paused = true;
            this.emit('pausedChanged', true);
            this.emit('error', { code: 'PLAYBACK_FAILED', message: '재개하지 못했다.', cause: e });
        }
    }
    /**
     * 앞뒤로 옮긴다 (절대 규칙 91). **컷을 넘지 않는다** — 판정은 `seekTarget` 하나다.
     *
     * ★ **`skip` 이벤트를 내지 않고 회차를 바꾸지 않는다.** 회차 끝 앞에서 서는 것이 그
     * 보장이다 — 넘게 두면 회차가 `skip` 없이 바뀌어 **이탈률이 줄어 보인다.**
     *
     * 떠 있던 상품 카드는 걷힌다(그 장면의 것이다 · D5). 걷힌 큐는 `firedCues` 에 있어
     * 되돌아가도 다시 뜨지 않는다. **새 위치를 먼저 적고 걷는다** — `OfferCues.close` 가 찍는 최소
     * 간격의 기준이 탐색 뒤의 자리여야 한다.
     */
    seekBy(deltaSec) {
        if (!this.canControlPlayback())
            return;
        const item = this.getCurrentItem();
        if (item === null)
            return;
        const target = seekTarget({
            elapsedSec: this.elapsedSec,
            deltaSec,
            durationSec: item.durationSec,
            nextCutSec: this.nextCutSec(item),
            forwardLocked: this.liveCut !== null,
        });
        if (target === this.elapsedSec)
            return;
        this.player.seek(target);
        this.elapsedSec = target;
        this.offerCards.close();
    }
    /**
     * 지금보다 뒤의 다음 컷(콘텐츠 축 초). **무장한 미드롤이 먼저다** — 그다음이 응답을 기다리는
     * 미드롤, 그다음이 아직 요청 전인 미드롤이다. 회차 사이 브레이크는 앵커가 회차 끝이라 여기 없다(`seekTarget` 이 끝을 본다).
     *
     * **발화 기록에 있는 큐는 건너뛴다** — no fill 이었던 미드롤은 컷이 없다.
     */
    nextCutSec(item) {
        const pending = this.pendingAd;
        if (pending !== null && pending.atSec !== null && pending.atSec > this.elapsedSec) {
            return pending.atSec;
        }
        // 응답을 기다리는 미드롤 — 광고가 올지 아직 모른다. 오면 그 자리를 잘라야 한다.
        if (this.midrollInFlight !== null && this.midrollInFlight > this.elapsedSec) {
            return this.midrollInFlight;
        }
        const policy = this.adPolicy;
        if (policy === null)
            return null;
        for (const cue of midrollCuePoints(policy, item.durationSec)) {
            if (this.firedCuePoints.has(cue))
                continue;
            if (cue > this.elapsedSec)
                return cue;
        }
        return null;
    }
    /**
     * 특허 ④. **새 플레이리스트를 받아 교체한다.** 기존 목록에서 인덱스만 옮기지 않는다 —
     * 그것이 "제2 플레이리스트" 구성의 실체이고, 새 `playlistId` 로 이후 이벤트가 묶인다.
     */
    async selectItem(productId) {
        // 2026-10-29 · 라이브 앞뒤 광고 중에는 같은 채널의 녹화물로 가지 않는다(채널 정책 · 화면이 같은 판정으로 알린다 · `docs/45` §18.8)
        if (this.recordedLocked())
            return;
        const gen = ++this.gen;
        this.player.cancelTailSwap();
        /*
         * ★ v33. **라이브 중에도 고를 수 있다** (`docs/67` §9.3). 막으면 그 순간 이 채널이
         * ONAIR 가 된다 — 고를 수 없는 것이 저 채널의 성질이다.
         *
         * ★★ **그 이벤트를 이번 창에서 포기한다.** 안 하면 `maybeEnterLive()` 가 다음
         * 틱(250ms)에 **도로 끌고 들어가고**, 시청자에게는 「눌렀는데 아무 일도 안 일어난다」다.
         * 채널을 다시 들어오거나 편성표의 라이브 상자를 누르면 그때 다시 합류한다(`stop()` 이
         * 비운다).
         */
        if (this.live !== null) {
            this.skippedEventIds.add(this.live.window.eventId);
            this.closeLive();
        }
        this.setState('switching');
        try {
            await this.playlist.fetch(productId);
        }
        catch (e) {
            this.fail({
                code: 'PLAYLIST_FETCH_FAILED',
                message: '선택한 상품의 플레이리스트를 받지 못했다.',
                cause: e,
            });
            return;
        }
        if (gen !== this.gen)
            return;
        const next = this.playlist.items;
        const first = next[0];
        if (!first) {
            this.setState('idle');
            this.emit('error', { code: 'EMPTY_PLAYLIST', message: '재생할 상품이 없다.' });
            return;
        }
        this.items = next;
        this.index = 0;
        this.pendingNextRound = null;
        this.emit('playlistLoaded', this.items);
        this.send('select', first);
        // v21. **`swapNow` 앞에 무장한다.** `none` 이면 아무 일도 없다.
        const kind = this.transitionKind('select');
        if (kind !== null)
            await this.openBreak(kind, null, true, { type: 'startCurrent' });
        if (gen !== this.gen)
            return;
        try {
            // 프리롤 리드가 없는 경로다. 스왑 전에 적재를 끝내 두면 어댑터가 `cold` 대신
            // 프리롤 없는 스왑(S3 수준)으로 처리할 수 있다.
            //
            // **선택한 회차에 진행이 있으면 그 지점부터다** (특허 【0100】). 서버가 새 목록의
            // `resume` 으로 실어 보내므로 아래 `resumeSecFor` 가 그것을 읽는다.
            //
            // v21. 광고가 열렸으면 **광고를 먼저 적재한다** — 복귀는 이 회차의 처음이다.
            const pending = this.pendingAd;
            const firstUrl = pending ? (pending.items[0]?.videoUrl ?? first.videoUrl) : first.videoUrl;
            const firstAt = pending ? undefined : this.resumeSecFor(first.productId);
            await this.player.prepareNext(firstUrl, firstAt);
            await this.player.swapNow('select');
        }
        catch (e) {
            // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
            if (gen !== this.gen)
                return;
            this.fail({ code: 'PLAYBACK_FAILED', message: '선택 전환이 실패했다.', cause: e });
            return;
        }
        if (gen !== this.gen)
            return;
        this.elapsedSec = 0;
        this.firedCuePoints.clear();
        this.midrollInFlight = null;
        // v23. **고른 회차의 큐로 갈아 끼운다.** 비우지 않으면 앞 회차의 `firedCues` 가 남아
        // **같은 회차를 다시 골랐을 때 카드가 하나도 안 뜬다** (`docs/47` §14 의 9).
        this.offerCards.reset();
        if (this.pendingAd !== null) {
            // 광고가 먼저다. **복귀는 이 회차의 처음**이고, `enterAdBreak` 이 그것을 무장한다.
            //
            // ★ 그래도 **회차는 먼저 알린다** — 알리지 않으면 광고 내내 앱의 `view.item` 이
            // 시청자가 방금 고른 것이 아니라 **직전에 보던 회차**다(절대 규칙 50 은 코어의
            // `getCurrentItem()` 을 이미 새 회차로 옮겨 두었다). 위 `startPendingBreakNow`
            // 와 같은 종류의 누락이고, 이쪽은 `null` 이 아니라 **틀린 값**이라 더 조용하다.
            this.emit('itemChanged', first, 0);
            // v23. **광고가 먼저여도 큐는 무장한다** — 브레이크가 끝나고 회차로 돌아오면 그
            // 시점부터 큐가 살아 있어야 한다. 관문이 광고 중에는 열지 않으므로 안전하다.
            // **고른 회차는 언제나 0초부터다**(`startFromProductId`) — 이어보기 지점이 아니다.
            this.offerCards.arm(first, 0);
            this.enterAdBreak(gen, null);
            return;
        }
        this.setState('playing');
        // **고른 회차는 언제나 0초부터다** (`startFromProductId`).
        this.offerCards.arm(first, 0);
        this.emit('itemChanged', first, 0);
        this.send('start', first);
        void this.armNext(gen);
    }
    /**
     * 구매하기·정보보기가 부른다. 현재 항목과 `playlistId` 에 자동으로 귀속된다.
     *
     * 호출자가 직접 `EventQueue` 를 만들지 않게 하려고 공개한다. 그러면 큐가 둘이 되어
     * 배치가 쪼개지고, 무엇보다 UI 가 `playlistId` 를 따로 들고 있어야 해서
     * 목록이 갱신되는 순간(끝 도달·선택) 엉뚱한 플레이리스트에 이벤트가 붙는다.
     *
     * **재생에 손대지 않는다.** 오버레이 토글은 호출자의 몫이다.
     */
    sendInteraction(type) {
        this.send(type, this.getCurrentItem());
    }
    getCurrentItem() {
        return this.items[this.index] ?? null;
    }
    /**
     * 실제로 사용 중인 `deviceId`. 저장소에 남아 있던 값일 수 있어 생성자 인자와 다르다.
     *
     * QR 추적 파라미터(`d=`)가 이 값을 써야 한다. UI 가 저장소를 직접 읽어 조립하면
     * 키가 두 곳에 생기고, 어긋나는 순간 "QR 을 스캔한 기기"와 "이벤트를 보낸 기기"가
     * 달라져 구매 전환 측정이 성립하지 않는다 — 이 MVP 가 증명해야 할 지표다.
     */
    getDeviceId() {
        return this.deviceId;
    }
    getPlaylist() {
        return this.items;
    }
    getCurrentIndex() {
        return this.index;
    }
    getState() {
        return this.state;
    }
    /**
     * 이 목록의 채널 종류 (v16). 목록을 받기 전에는 `null` 이다.
     *
     * **요청에 실은 값이 아니라 응답의 값이다** — `getChannelId()` 와 같은 방침이다.
     */
    getChannelKind() {
        return this.playlist.channelKind;
    }
    /**
     * 이어볼 지점 (v16). **홈쇼핑 채널이면 서버가 언제나 `null` 을 준다**(D5).
     *
     * 화면이 토스트를 띄우는 근거이고, 재생을 시작하는 근거는 아니다 — 그쪽은 이미
     * `start()` 안에서 어댑터로 넘어갔다.
     */
    getResume() {
        return this.playlist.resume;
    }
    getPlaylistId() {
        return this.playlist.playlistId;
    }
    /**
     * v21. 이 채널의 광고 편성 정책. **`null` 이면 광고가 없다.**
     *
     * **요청에 실은 값이 아니라 응답의 값이다** — `getChannelKind()` 와 같은 방침이고,
     * 셋 중 하나만 어긋나도 서버가 `null` 을 준다 (`docs/45` §5.2).
     */
    getAdPolicy() {
        return this.adPolicy;
    }
    /**
     * 지금 항목의 경과 초 (v12).
     *
     * **상품이 바뀌는 네 경로에서 0 으로 돌아간다** — `start`·`selectItem`·`afterSwap`·
     * `recoverByLoad`. 그래서 「이 상품이 얼마나 지났는가」이지 채널 전체의 경과가 아니다.
     *
     * ── 왜 게터가 필요했나 ──────────────────────────────────────
     *
     * 화면이 남은 시간을 그리려면 이 값이 필요한데, **`PlayerAdapter.onTimeUpdate` 를 UI 가
     * 쓸 수 없다** — 콜백 슬롯이 하나이고 이 클래스가 점유하고 있어서, 덮으면 `elapsedSec`
     * 이 멈춰 **`canSkip()` 이 영원히 false 가 된다** (`useReceiver.ts` 의 그 주석).
     *
     * 남은 길은 `<video>.currentTime` 을 화면이 직접 읽는 것뿐인데, 더블 버퍼라 꼬리 프리롤
     * 구간에서 두 엘리먼트가 동시에 재생되어 **항목마다 시계가 한 번씩 튄다.** 가르려면
     * 어댑터 내부를 들여다봐야 하고 그것은 계층 분리를 깨는 방향이다.
     *
     * 그래서 이 게터다 — DOM 도 새 타이머도 어댑터 계약도 건드리지 않는다.
     * 값이 갱신되는 주기는 어댑터의 `timeupdate`(약 4Hz)이므로 그보다 자주 읽어도 얻을
     * 것이 없다.
     */
    getElapsedSec() {
        return this.elapsedSec;
    }
    /**
     * 이 항목을 이어볼 지점(초). 없으면 `undefined` — 처음부터다 (v16).
     *
     * **서버가 정한 값을 그대로 쓴다.** `items[].progress` 가 아니라 `resume` 을 보는 이유는
     * 「어디까지 봤는가」와 「어디서 이어볼 것인가」가 다르기 때문이다 — 마지막 회차를 끝까지
     * 본 시청자의 `resume` 은 **다음 회차의 0초**이고, 그 판정은 서버의 것이다
     * (`playlist/resume.ts`, 절대 규칙 46).
     *
     * **홈쇼핑 채널에서는 서버가 언제나 `null` 을 준다**(D5) — 그래서 이 값이 붙는 것만으로
     * 홈쇼핑의 재생 경로가 달라지지 않는다.
     */
    resumeSecFor(productId) {
        const resume = this.playlist.resume;
        if (resume === null || resume.productId !== productId)
            return undefined;
        return resume.positionSec > 0 ? resume.positionSec : undefined;
    }
    /**
     * **광고 중에는 광고의 정책을 본다** (`docs/45` §8.5). 그대로 두면 **드라마의
     * `minWatchSec` 으로 광고 스킵 버튼이 켜진다.**
     */
    canSkip() {
        if (this.adBreak !== null)
            return this.canSkipAd();
        return canSkip(this.getCurrentItem(), this.elapsedSec);
    }
    /** 건너뛰기 버튼의 남은 초. 스킵 금지 항목은 `null` (버튼을 숨겨야 한다). */
    secondsUntilSkippable() {
        if (this.adBreak !== null)
            return this.secondsUntilAdSkippable();
        return secondsUntilSkippable(this.getCurrentItem(), this.elapsedSec);
    }
    // ── v21. 광고 공개 표면 다섯 ────────────────────────────────────────────
    /**
     * **화면이 광고 UI 를 그리는 유일한 근거다** (절대 규칙 50).
     *
     * `getCurrentItem()` 은 광고 중에도 **콘텐츠**를 돌려준다 — 광고를 돌려주면 `send()` 가
     * `event.productId` 에 크리에이티브 id 를 실어 그것이 `Event` 로 흘러가고,
     * `EVENT_TYPES` 를 늘리지 않기로 한 것이 아무 소용이 없어진다.
     */
    getAdBreak() {
        const state = this.adBreak;
        const item = state?.items[state.index];
        if (!state || !item)
            return null;
        return {
            kind: state.kind,
            totalSec: state.totalSec,
            index: state.index,
            count: state.items.length,
            item,
            // **미드롤만 돌아갈 자리가 있다.** 그 밖은 다음 회차이거나 이 회차의 처음이다.
            resumeAtSec: state.returnTo.type === 'resume' ? state.returnTo.atSec : null,
        };
    }
    isInAdBreak() {
        return this.adBreak !== null;
    }
    canSkipAd() {
        const state = this.adBreak;
        const ad = state?.items[state.index];
        if (!state || !ad || ad.skipOffsetSec === null)
            return false;
        return this.elapsedSec >= ad.skipOffsetSec;
    }
    /** 광고 스킵 버튼의 남은 초. 건너뛸 수 없는 광고는 `null` */
    secondsUntilAdSkippable() {
        const state = this.adBreak;
        const ad = state?.items[state.index];
        if (!state || !ad || ad.skipOffsetSec === null)
            return null;
        return Math.max(0, Math.ceil(ad.skipOffsetSec - this.elapsedSec));
    }
    /**
     * 브레이크 축의 경과(초). 광고가 아니면 `0`.
     *
     * **게터로 두고 `AdBreakView` 에 싣지 않는다** — 시계가 250ms 로 읽는 값이라, 뷰에
     * 실으면 `getAdBreak()` 이 매 틱 새 객체를 만들고 `useEffect` 의존성이 매번 달라진다
     * (`getElapsedSec` 이 이미 같은 이유로 게터다).
     *
     * **화면이 이것을 다시 계산하면 스킵 점프 보정까지 두 벌**이 되고, 어긋난 시계는
     * **아무 오류도 내지 않는다** (D15 가 막으려는 그 어긋남이다).
     */
    getAdBreakElapsedSec() {
        const state = this.adBreak;
        if (state === null)
            return 0;
        /*
         * **지나온 광고가 실제로 나간 초 + 지금 광고의 경과** — 벽시계처럼 흐른다(2026-10-28 · `docs/45` §17.3). 스킵해도 뛰지
         * 않고, 재보충이 붙어도 `totalSec`(약속한 길이)이 늘지 않으므로 남은 시간은 일정하게만 준다. 자투리를 못 채우면 0 이
         * 되기 전에 회차로 간다. 전에는 지나온 광고의 **길이** 합이라 스킵하면 남은 시간이 건너뛴 몫만큼 뛰었다(§8.5).
         */
        return state.playedBeforeSec + this.elapsedSec;
    }
    on(event, cb) {
        const set = this.listeners.get(event) ?? new Set();
        set.add(cb);
        this.listeners.set(event, set);
    }
    off(event, cb) {
        this.listeners.get(event)?.delete(cb);
    }
    // ── 내부 ───────────────────────────────────────────────────────────────
    /**
     * 다음 항목을 적재하고 꼬리 스왑을 무장한다. **현재 항목 재생 시작 직후** 부른다 —
     * 영상이 끝날 때까지 기다리지 않는다. 스킵은 언제든 들어온다.
     */
    async armNext(gen) {
        const nextUrl = await this.resolveNextUrl(gen);
        if (gen !== this.gen || nextUrl === null)
            return;
        // 적재 실패를 여기서 처리하지 않는다. 꼬리 시점에 어댑터가 `no-preroll`·`watchdog`·
        // `cold` 로 강등해 처리하고 `SwapPath` 로 보고한다. 여기서 에러를 내면 아직 재생 중인
        // 화면에 오류 UI 가 뜬다.
        void this.player.prepareNext(nextUrl).catch(() => undefined);
        this.player.armTailSwap({
            prerollSec: this.prerollSec,
            onSwapped: (path, cutAtSec) => {
                if (gen !== this.gen)
                    return;
                /*
                 * ★ v21. **광고가 끼어드는 스왑에서는 회차가 끝난 것이 아니다.**
                 *
                 * 이 호출은 `afterSwap()` **앞**에 조건 없이 있었다. 그대로 두면 광고 3편짜리
                 * pod 하나에 그 회차의 `complete` 가 **넷** 쌓인다 — 컷에서 하나, 광고 사이에서
                 * 둘, 복귀에서 하나. 그리고 `getCurrentItem()` 이 콘텐츠로 고정돼 있으므로
                 * **넷 다 드라마 회차에 귀속된다.**
                 *
                 * 화면에는 아무 증상이 없고 **완료율에서만** 갈린다 (`docs/45` §14 의 22).
                 */
                if (this.adBreak === null && this.pendingAd === null) {
                    this.send('complete', this.getCurrentItem());
                }
                this.afterSwap(path, cutAtSec ?? null);
            },
            onAbandoned: (reason) => {
                if (gen !== this.gen)
                    return;
                this.emit('error', {
                    code: 'TRANSITION_ABANDONED',
                    message: `꼬리 시점에 전환이 준비되지 않았다: ${reason}`,
                });
                void this.recoverByLoad(gen);
            },
        });
    }
    /**
     * 다음에 재생할 URL. 마지막 항목이면 **여기서** 다음 회차 목록을 미리 받는다.
     *
     * 재요청이 실패하거나 빈 목록이면 현재 목록을 처음부터 다시 돈다. 재생이 이어지고 있는
     * 상황이라 `error` 를 내지 않는다 — M6 이 `error` 를 오류 화면으로 그리므로 멀쩡히
     * 재생 중인 채널에 오류 UI 가 뜨게 된다. 대신 `playlistId` 가 갱신되지 않는다.
     */
    async resolveNextUrl(gen) {
        const isLast = this.index === this.items.length - 1;
        if (!isLast) {
            return this.items[this.index + 1]?.videoUrl ?? null;
        }
        try {
            const res = await this.playlist.fetch();
            if (gen !== this.gen)
                return null;
            if (res.items.length > 0) {
                this.pendingNextRound = { playlistId: res.playlistId, items: res.items };
                return res.items[0]?.videoUrl ?? null;
            }
        }
        catch {
            // 아래 폴백으로 떨어진다.
        }
        if (gen !== this.gen)
            return null;
        this.pendingNextRound = null;
        return this.items[0]?.videoUrl ?? null;
    }
    /** 스왑이 끝난 뒤의 인덱스 진행·이벤트. 자연 종료와 스킵이 공유한다. */
    afterSwap(path, cutAtSec) {
        void path;
        /*
         * v21. **이 두 분기가 없으면** 「화면은 광고인데 코어는 다음 회차를 재생 중으로 여기고
         * `start` 이벤트까지 낸다」 — 그리고 그 회차의 진행이 광고 시간만큼 앞서 기록된다.
         */
        if (this.adBreak !== null) {
            this.advanceAd(this.gen);
            return;
        }
        if (this.pendingAd !== null) {
            this.enterAdBreak(this.gen, cutAtSec);
            return;
        }
        this.advanceContent(this.gen);
    }
    /** 다음 회차로 진행한다. **광고가 있든 없든 같은 코드다** */
    advanceContent(gen) {
        this.firedCuePoints.clear();
        this.midrollInFlight = null;
        // v23. **회차가 바뀌는 자리다.** `stop()`·`selectItem()` 과 함께 셋이고, 하나라도
        // 빠지면 같은 회차를 다시 볼 때 카드가 **하나도** 안 뜬다 (`docs/47` §14 의 9).
        this.offerCards.reset();
        if (this.index === this.items.length - 1 && this.pendingNextRound) {
            this.items = this.pendingNextRound.items;
            this.pendingNextRound = null;
            this.emit('playlistLoaded', this.items);
            this.index = 0;
        }
        else {
            this.index = (this.index + 1) % this.items.length;
        }
        const item = this.items[this.index];
        if (!item)
            return;
        this.elapsedSec = 0;
        this.setState('playing');
        // 자연 전환 — **다음 회차는 처음부터다.**
        this.offerCards.arm(item, 0);
        this.emit('itemChanged', item, this.index);
        this.send('start', item);
        void this.armNext(gen);
    }
    /** 어댑터가 전환을 포기했을 때의 복구. 무갭은 포기하고 재생 연속성만 지킨다. */
    async recoverByLoad(gen) {
        /*
         * ★ v21. **광고 중이면 복귀 지점으로 돌아간다** (`docs/45` §14 의 7).
         *
         * 그대로 두면 `(index + 1)` 이라 **광고가 조용히 사라지고 다음 회차로 간다** —
         * 임프레션 누락인데 **화면에는 증상이 없다.**
         */
        if (this.adBreak !== null) {
            await this.closeAdBreak(gen, true);
            return;
        }
        const nextIndex = (this.index + 1) % this.items.length;
        const item = this.items[nextIndex];
        if (!item)
            return;
        try {
            await this.player.load(item.videoUrl);
            await this.player.play();
        }
        catch (e) {
            // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
            if (gen !== this.gen)
                return;
            this.fail({ code: 'PLAYBACK_FAILED', message: '복구 재생이 실패했다.', cause: e });
            return;
        }
        if (gen !== this.gen)
            return;
        this.index = nextIndex;
        this.elapsedSec = 0;
        this.setState('playing');
        // v23. **복구도 회차가 바뀌는 자리다.** 설계가 센 셋(`stop`·`selectItem`·`afterSwap`)에
        // 없지만 여기도 인덱스가 오르므로, 빠뜨리면 **복구를 거친 회차에서만** 앞 회차의
        // `firedCues` 가 살아남는다 — 그리고 그 경로는 어댑터가 전환을 포기했을 때만 도므로
        // **재현이 들쭉날쭉하다.**
        this.offerCards.reset();
        this.offerCards.arm(item, 0);
        this.emit('itemChanged', item, this.index);
        this.send('start', item);
        void this.armNext(gen);
    }
    // ── v21. 광고 ──────────────────────────────────────────────────────────
    get adPolicy() {
        return this.playlist.adPolicy;
    }
    /** 조작 정책 → 브레이크 종류. `none` 이면 `null` 이고 아무 일도 없다 */
    transitionKind(trigger) {
        const policy = this.adPolicy;
        if (policy === null)
            return null;
        return transitionBreakKind(policy, trigger);
    }
    /**
     * ★ **브레이크가 열리는 유일한 관문이다.** 다섯 자리(프리롤·미드롤·회차 사이·건너뛰기·
     * 목록 선택)가 전부 여기를 지난다 (`docs/45` §8.4).
     *
     * **흩으면 「목록 선택에서만 최소 간격이 안 걸린다」 같은 것이 생기고, 그런 구멍은
     * 정확히 그 경로를 밟는 시연에서만 드러난다.**
     *
     * 판정 다섯을 한 자리에서 한다 — 정책 유무 · **재귀 없음**(규칙 ①) · 길이가 0 이 아님 ·
     * **최소 간격**(규칙 ③) · no fill.
     *
     * **실패는 조용하다.** `false` 를 돌려줄 뿐 `emit('error')` 하지 않는다 — 광고가 안
     * 나오는 것은 시청자에게 사고가 아니다 (`docs/45` §8.7).
     */
    async openBreak(kind, atSec, blocking, returnTo) {
        const policy = this.adPolicy;
        if (policy === null || this.ads === null)
            return false;
        // 규칙 ① — **광고 중에는 브레이크가 열리지 않는다.** 재귀가 없다.
        if (this.adBreak !== null || this.pendingAd !== null)
            return false;
        const maxDurationSec = breakDurationSec(policy, kind);
        if (maxDurationSec <= 0)
            return false;
        // 규칙 ③ — **최소 간격이 모든 종류보다 세다.** 거르는 것이지 미루는 것이 아니다.
        if (!this.gapElapsed(policy))
            return false;
        // 2026-10-29 · 라이브 앞뒤 광고 둘레에서는 녹화물 광고를 열지 않는다 — 시작 전 광고가 이기고, 끝난 뒤 `liveAdGapSec` 은 쉰다(`docs/45` §18.8)
        if (recordedBreakQuiet({
            nowMs: this.liveNow(),
            windows: this.liveWindows,
            skippedEventIds: this.skippedEventIds,
            breakSec: maxDurationSec,
            liveBeforeSec: policy.liveBeforeSec,
            gapSec: policy.liveAdGapSec,
            lastLiveAdEndedAtMs: this.lastLiveAdEndedAtMs,
        })) {
            return false;
        }
        const current = this.getCurrentItem();
        this.correlator += 1;
        const items = await this.ads.requestAds({
            adServerUrl: policy.adServerUrl,
            channelId: this.playlist.servedChannelId ?? '',
            breakKind: kind,
            maxDurationSec,
            productId: current?.productId ?? '',
            positionSec: Math.max(0, Math.floor(this.elapsedSec)),
            /*
             * ★ **채널이 정한 광고 언어** (v21 확장). **시청자의 UI 언어가 아니다** —
             * 영어 드라마 채널은 한국어로 앱을 보는 사람에게도 영어 광고를 낸다.
             * `@apm/i18n` 의 로케일을 여기 잇고 싶어지는 자리이고, 그러면 광고 언어가
             * **브라우저 설정**에 달린다.
             *
             * **`?? 'ko'` 는 타입상 불가능한 것을 막는다.** 이 값은 서버가 준 플레이리스트에서
             * 오는데, **옛 서버의 응답에는 이 키가 없다.** 그때 `lang=undefined` 가 나가면
             * 광고 서버가 400 을 주고 **광고가 통째로 사라지는데 오류 화면도 안 뜬다.**
             */
            language: policy.language ?? 'ko',
            deviceId: this.deviceId,
            playlistId: this.playlist.playlistId ?? '',
            correlator: this.correlator,
        }, { timeoutMs: blocking ? AD_REQUEST_BLOCKING_TIMEOUT_MS : AD_REQUEST_TIMEOUT_MS });
        // no fill · 실패 · 타임아웃 — **셋이 같은 길이다.** 새 분기가 없다.
        if (items.length === 0)
            return false;
        // 기다리는 사이에 다른 브레이크가 열렸을 수 있다. 재귀 금지를 한 번 더 본다.
        if (this.adBreak !== null || this.pendingAd !== null)
            return false;
        this.pendingAd = { kind, atSec, items, returnTo };
        return true;
    }
    /** `minGapSec` 이 지났는가. **채널을 가로지른다** — 값이 저장소에서 온다 */
    gapElapsed(policy) {
        if (policy.minGapSec <= 0)
            return true;
        if (this.lastBreakEndedAtMs === null)
            return true;
        return Date.now() - this.lastBreakEndedAtMs >= policy.minGapSec * 1000;
    }
    /**
     * 예약형 둘을 여는 자리. **`onTimeUpdate` 축(약 4Hz)에서 돈다** (`docs/45` §8.4 의 ★).
     *
     * 둘이 한 회차에서 겹칠 수 있다 — 끝에서 20초 안에 큐 포인트가 남아 있는 회차다.
     * 그때 뒤엣것은 `openBreak` 의 재귀 금지와 `minGapSec` 이 거른다.
     */
    maybeOpenScheduledBreak() {
        if (this.state !== 'playing')
            return;
        // v33. **바이패스 중에는 열리지 않는다** (절대 규칙 85). 화면에 있는 것은 콘텐츠가
        // 아니고, `elapsedSec` 도 라이브의 초다 — 큐 포인트 비교가 애초에 성립하지 않는다.
        if (this.live !== null)
            return;
        if (this.adBreak !== null || this.pendingAd !== null)
            return;
        const policy = this.adPolicy;
        const item = this.getCurrentItem();
        if (policy === null || item === null)
            return;
        // ① 미드롤 — 아직 안 지난 첫 큐 포인트의 20초 전
        for (const cue of midrollCuePoints(policy, item.durationSec)) {
            if (this.firedCuePoints.has(cue))
                continue;
            if (this.elapsedSec < cue - AD_REQUEST_LEAD_SEC)
                break;
            // **여기서 발화 표시를 한다.** 요청이 실패해도 다시 쏘지 않는다 — 4Hz 로 도는
            // 축이라 표시하지 않으면 컷까지 80번 요청한다.
            this.firedCuePoints.add(cue);
            // v35. 응답이 올 때까지 탐색의 상한이 이 큐다 — 근거는 `midrollInFlight` 주석.
            this.midrollInFlight = cue;
            // **미드롤은 잘린 초로 돌아온다** — 왕복 불변식이 여기서 코어까지 이어진다.
            void this.openBreak('midroll', cue, false, { type: 'resume', atSec: null }).then((opened) => {
                if (this.midrollInFlight === cue)
                    this.midrollInFlight = null;
                if (opened)
                    this.armPendingBreak(this.gen);
            });
            return;
        }
        // ② 회차 사이 — 영상 끝의 20초 전. **앵커가 영상 끝이라 `atSec` 이 없다**
        if (item.durationSec > 0 && this.elapsedSec >= item.durationSec - AD_REQUEST_LEAD_SEC) {
            // **앵커가 영상 끝이라 `atSec` 이 없다.** 끝나면 다음 회차다.
            void this.openBreak('between_episodes', null, false, { type: 'advance' }).then((opened) => {
                if (opened)
                    this.armPendingBreak(this.gen);
            });
        }
    }
    /**
     * ★ **예약형 브레이크의 컷을 무장한다.** `openBreak` 이 성공한 직후에만 부른다.
     *
     * 이 한 줄이 없으면 **미드롤이 조용히 안 나온다** — `pendingAd` 만 채워지고 컷이
     * 오지 않아 회차가 끝까지 가고, 다음 회차에서 `afterSwap` 이 그 pending 을 소비해
     * **엉뚱한 자리에서 광고가 나온다.**
     *
     * **`armNext()` 가 이미 걸어 둔 꼬리 무장을 덮는다.** 그것이 의도다 — 미드롤은 영상
     * 끝이 아니라 `atSec` 에서 잘라야 하고, 대기 버퍼도 다음 회차가 아니라 **광고**를 물어야
     * 한다. 브레이크가 끝나면 `closeAdBreak` 이 `armNext()` 로 되돌린다.
     */
    armPendingBreak(gen) {
        if (gen !== this.gen)
            return;
        const pending = this.pendingAd;
        const first = pending?.items[0];
        if (!pending || !first)
            return;
        void this.player.prepareNext(first.videoUrl).catch(() => undefined);
        /*
         * ★ **회차 끝에서 자르는 브레이크만 `complete` 를 낸다** (v21/F13 실기가 찾았다).
         *
         * 여기 오는 예약형은 둘이고 **끝나는 자리가 다르다** — 미드롤은 회차 **중간**
         * (`atSec` 이 있다), 회차 사이는 회차의 **끝**(`atSec` 이 없다. 앵커가 영상 끝이다).
         *
         * 앞은 회차가 끝난 것이 아니라 `complete` 를 내면 안 되고(§14 의 22), **뒤는 회차가
         * 실제로 끝난 자리라 내야 한다.** 둘을 한 조건으로 묶어 통째로 막았더니 **회차 사이
         * 광고를 켠 순간 그 채널의 완료율이 0 이 됐다** — 광고가 없으면 `armNext` 의 스왑이
         * 내주므로 **광고를 켠 배포에서만** 그렇고, 화면에는 아무 증상이 없다.
         */
        const atEpisodeEnd = pending.atSec === null;
        this.player.armTailSwap({
            prerollSec: this.prerollSec,
            // **정책상 초를 준다.** 어댑터가 그것을 청크 경계로 내리고, 내린 값을 `cutAtSec`
            // 으로 되돌려 준다 (절대 규칙 47·52).
            ...(pending.atSec !== null ? { atSec: pending.atSec } : {}),
            onSwapped: (path, cutAtSec) => {
                if (gen !== this.gen)
                    return;
                // 미드롤이면 내지 않는다 — 회차가 끝난 것이 아니다 (`armNext` 의 ★).
                if (atEpisodeEnd)
                    this.send('complete', this.getCurrentItem());
                this.afterSwap(path, cutAtSec ?? null);
            },
            onAbandoned: () => {
                // 버려졌으면 **다시 무장할 수 있어야 한다** — 그러지 않으면 즉시형만 남는다.
                this.liveCut = null;
                if (gen !== this.gen)
                    return;
                // **브레이크를 버리고 회차를 계속 간다.** 광고는 재생을 막지 않는다.
                this.pendingAd = null;
                void this.armNext(gen);
            },
        });
    }
    /**
     * 즉시형 브레이크를 **재생 전에** 튼다 (프리롤 · 목록 선택의 첫 진입).
     * 아직 아무것도 재생하지 않은 상태라 `load` + `play` 다.
     */
    async startPendingBreakNow(gen, contentItem) {
        const pending = this.pendingAd;
        const firstAd = pending?.items[0];
        if (!pending || !firstAd)
            return;
        try {
            await this.player.load(firstAd.videoUrl);
            await this.player.play();
        }
        catch (e) {
            // **광고가 재생 실패면 브레이크를 버리고 콘텐츠로 간다** — 광고는 재생을 막지 않는다.
            this.pendingAd = null;
            await this.player.load(contentItem.videoUrl, this.resumeSecFor(contentItem.productId));
            await this.player.play();
            if (gen !== this.gen)
                return;
            // ★ **튼 자리로 시작한다** (위 `start()` 의 ★★ 와 같은 이유).
            this.elapsedSec = this.resumeSecFor(contentItem.productId) ?? 0;
            this.setState('playing');
            this.emit('itemChanged', contentItem, this.index);
            this.send('start', contentItem);
            void this.armNext(gen);
            void e;
            return;
        }
        if (gen !== this.gen)
            return;
        /*
         * ★ **광고보다 먼저 「지금 회차가 무엇인가」를 알린다** (F13 실기가 찾았다).
         *
         * 이 경로에서만 `itemChanged` 가 한 번도 나가지 않았다 — 채널 진입 프리롤은
         * `start()` 의 아래쪽 `emit` 을 **건너뛰고** 여기서 끝나기 때문이다. 그러면 앱의
         * `view.item` 이 `null` 인 채라 `WatchScreen` 의 `view.started && item !== null`
         * 블록이 통째로 렌더되지 않고, **프리롤 동안 시계도 광고 구매 버튼도 광고 스킵
         * 버튼도 없다.** 영상만 나오므로 「광고가 잘 나온다」로 보인다.
         *
         * **`send('start')` 은 여기서 내지 않는다** — 회차가 시작한 것이 아니다. 그것은
         * 복귀(`startCurrent`)가 낸다.
         */
        this.emit('itemChanged', contentItem, this.index);
        this.enterAdBreak(gen, null);
    }
    /**
     * 컷이 일어났다 — pod 의 첫 광고가 화면에 있다.
     *
     * `cutAtSec` 은 **어댑터가 실제로 자른 초**(청크 경계로 내려간 값)다. 미드롤은 그 값
     * **그대로** 복귀한다 — 자른 곳과 돌아오는 곳이 같아야 한 프레임도 다시 보지 않는다
     * (절대 규칙 52).
     */
    enterAdBreak(gen, cutAtSec) {
        const pending = this.pendingAd;
        const first = pending?.items[0];
        if (!pending || !first)
            return;
        this.pendingAd = null;
        /*
         * ★ v23. **카드를 먼저 걷는다** (절대 규칙 64 · `docs/47` §8.5).
         *
         * 걷지 않으면 **광고 위에 상품 카드가 겹친다** — 광고주에게는 화면 침범이고 우리에게는
         * 오염된 임프레션인데, **화면에는 「좀 지저분하다」로만 보인다.**
         *
         * ★★ **호출부가 아니라 여기 둔 것이 결정이다.** 설계는 「`OfferCues.close()` 가
         * `enterAdBreak()` 앞에 선다」고 적는데, 부르는 자리가 셋이라(`afterSwap` ·
         * `startPendingBreakNow` · `selectItem`) **하나만 빠뜨려도 그 경로에서만 겹친다.**
         * 첫 줄에 두면 빠뜨릴 자리가 없다.
         *
         * **`pendingOffer` 도 함께 버린다** — 요청은 나갔지만 아직 안 뜬 카드가 광고 위에
         * 뜨는 것이 같은 결함이다. 광고가 끝나고 아직 창 안이면 그때 다시 묻는다.
         */
        this.offerCards.close();
        this.adBreak = {
            kind: pending.kind,
            items: pending.items,
            index: 0,
            // **처음 편성된 광고들의 길이 합이다. 정책 길이가 아니다** (D9) — 광고 서버가 덜 채울 수 있다. 시청자에게 약속한
            // 길이이고 **재보충이 붙어도 늘지 않는다**(재보충은 건너뛴 시간을 메울 뿐이다 · `docs/45` §17.3).
            totalSec: pending.items.reduce((sum, ad) => sum + ad.durationSec, 0),
            // **미드롤은 어댑터가 실제로 자른 초로 돌아간다** — 정책상 시점이 아니다.
            returnTo: pending.returnTo.type === 'resume'
                ? { type: 'resume', atSec: cutAtSec ?? pending.atSec }
                : pending.returnTo.type === 'live'
                    ? { ...pending.returnTo, atSec: cutAtSec ?? pending.atSec }
                    : pending.returnTo,
            freedSec: 0,
            playedBeforeSec: 0,
            refill: null,
            skipping: false,
            liveArmed: false,
        };
        this.elapsedSec = 0;
        this.setState('playing');
        this.trackAdStart(first);
        this.emit('adBreakChanged', this.getAdBreak());
        this.armAdNext(gen);
    }
    /** 다음 광고 또는 복귀를 무장한다. **광고와 광고 사이도 꼬리 스왑이다** */
    armAdNext(gen) {
        const state = this.adBreak;
        if (!state)
            return;
        const next = state.items[state.index + 1];
        // 마지막 광고면 **복귀**를 적재한다. 무엇으로 돌아가는지는 `returnTo` 가 정한다.
        // 2026-10-29 — 시작 전 광고의 마지막이면 창이 열렸을 때 **라이브 스트림**을 문다(창 전의 스트림은 503 이다 · 그때는 자른 자리).
        const intoLive = !next && state.returnTo.type === 'live' && this.liveWindowOpen(state.returnTo.window);
        state.liveArmed = intoLive;
        const target = next
            ? { url: next.videoUrl, at: undefined }
            : intoLive && state.returnTo.type === 'live'
                ? { url: state.returnTo.window.streamUrl, at: undefined }
                : this.returnTarget(state.returnTo);
        const url = target.url;
        if (url === null)
            return;
        const at = target.at;
        void this.player.prepareNext(url, at).catch(() => undefined);
        this.player.armTailSwap({
            prerollSec: this.prerollSec,
            onSwapped: () => {
                if (gen !== this.gen)
                    return;
                // **`complete` 를 내지 않는다** — 회차가 끝난 것이 아니다(위 `armNext` 의 ★).
                this.afterSwap('preroll', null);
            },
            onAbandoned: () => {
                // 버려졌으면 **다시 무장할 수 있어야 한다** — 그러지 않으면 즉시형만 남는다.
                this.liveCut = null;
                if (gen !== this.gen)
                    return;
                // **오류를 내지 않는다.** 광고 실패는 조용하고, 복귀 지점으로 돌아간다.
                void this.recoverByLoad(gen);
            },
        });
    }
    /**
     * 광고 하나가 끝났다 — 다음 광고이거나 복귀다.
     *
     * @param playedSec 그 광고가 실제로 나간 초. 건너뛰었으면 넘어간 순간의 경과이고, 없으면 끝까지 본 것(그 길이)이다
     */
    advanceAd(gen, playedSec) {
        const state = this.adBreak;
        if (!state)
            return;
        const ended = state.items[state.index];
        if (ended)
            state.playedBeforeSec += playedSec ?? ended.durationSec;
        const next = state.items[state.index + 1];
        if (!next) {
            void this.closeAdBreak(gen, false);
            return;
        }
        state.index += 1;
        this.elapsedSec = 0;
        this.trackAdStart(next);
        this.emit('adBreakChanged', this.getAdBreak());
        this.armAdNext(gen);
    }
    /**
     * pod 이 끝났다 — 콘텐츠로 돌아간다.
     *
     * **`lastBreakEndedAtMs` 를 여기서만 쓴다.** 저장소에 남겨야 채널을 바꿔도 `minGapSec`
     * 이 산다 (`RECEIVER_LAST_AD_BREAK_KEY` 주석).
     */
    async closeAdBreak(gen, needsLoad) {
        const state = this.adBreak;
        if (!state)
            return;
        const returnTo = state.returnTo;
        this.adBreak = null;
        this.markBreakEnded();
        if (state.kind === 'live_before' || state.kind === 'live_after') {
            this.lastLiveAdEndedAtMs = this.liveNow();
        }
        this.emit('adBreakChanged', null);
        // 2026-10-29 · 시작 전 광고가 끝났다 — 창이 열렸으면 그 라이브로(`docs/45` §18)
        if (returnTo.type === 'live' && this.liveWindowOpen(returnTo.window)) {
            await this.enterLiveAfterBreak(gen, returnTo, state.liveArmed && !needsLoad);
            return;
        }
        const target = this.returnTarget(returnTo);
        if (target.url === null)
            return;
        /*
         * **정상 경로에서는 이미 콘텐츠가 화면에 있다.** 마지막 광고의 꼬리 스왑이 대기
         * 버퍼(= 복귀 대상)를 앞으로 내보냈고, 이 함수는 그 뒤에 불린다.
         *
         * `needsLoad` 가 참인 것은 **어댑터가 전환을 포기했을 때**뿐이다 — 그때는 화면이
         * 광고의 마지막 프레임에 멈춰 있으므로 직접 적재한다 (`docs/45` §14 의 7).
         */
        if (needsLoad) {
            try {
                await this.player.load(target.url, target.at);
                await this.player.play();
            }
            catch (e) {
                // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
                if (gen !== this.gen)
                    return;
                this.fail({ code: 'PLAYBACK_FAILED', message: '광고 뒤 복귀가 실패했다.', cause: e });
                return;
            }
            if (gen !== this.gen)
                return;
        }
        if (returnTo.type === 'advance') {
            // 광고가 없었을 때와 **같은 진행**이다 — 인덱스를 올리고 `start` 를 낸다.
            this.advanceContent(gen);
            return;
        }
        // 2026-10-29 · 종료 후 광고가 끝났다 — 녹화물의 그 자리로(`leaveLive` 의 복귀와 같은 모양 · `docs/45` §18)
        if (returnTo.type === 'fromLive') {
            const item = this.getCurrentItem();
            if (!item)
                return;
            this.elapsedSec = target.at ?? 0;
            this.setState('playing');
            this.offerCards.reset();
            this.offerCards.arm(item, this.elapsedSec);
            this.emit('itemChanged', item, this.index);
            if (returnTo.sendStart)
                this.send('start', item);
            void this.armNext(gen);
            return;
        }
        if (returnTo.type === 'startCurrent') {
            const item = this.getCurrentItem();
            if (!item)
                return;
            /*
             * ★★ **튼 자리로 시작한다. 0 이 아니다** (v28/TG21, 2026-09-05 무대 신고).
             *
             * TG18 이 `start()` 에서 고친 그것인데, **그 감사가 이 갈래를 빼먹었다.** 당시
             * 주석은 「광고 복귀 경로는 이미 이렇게 하고 있었다」고 적었지만 그것은 아래
             * `resume` 하나였고, **`startCurrent` 는 여기서 0 으로 되돌리고 있었다** —
             * 그리고 이 갈래가 **프리롤과 진입 브레이크의 복귀 전부**다.
             *
             * 화면에 무엇이 있는지는 `armAdNext` 가 정한다 — 마지막 광고의 대기 버퍼를
             * `returnTarget()` 의 `at`(= 이어보기 지점)으로 `prepareNext` 한다. **그래 놓고
             * 여기서 0 을 말하면 첫 `timeupdate` 까지 `getElapsedSec()` 이 거짓말한다.**
             *
             * 무대 기록(2026-09-05 11:51): 채널 2 진입 프리롤이 끝나고 `start@0` 이 나갔는데
             * **17.9초 뒤 같은 회차가 `complete@299`** 였다 — 본편은 약 281초에 있었다.
             * 시청자에게는 컨트롤 바의 경과와 편성표 행이 **한 번 크게 튀는 것**으로 보인다.
             *
             * ★ **값은 `returnTarget()` 이 쓴 것 하나다.** 여기서 `resumeSecFor` 를 다시
             * 부르면 「적재한 자리」와 「말하는 자리」가 두 벌이 되고, 갈리는 날 증상이 정확히
             * 이것이다. 아래 `resume` 갈래도 같은 값을 쓴다.
             */
            const startAtSec = target.at ?? 0;
            this.elapsedSec = startAtSec;
            this.setState('playing');
            // v23. 회차를 **적재한 자리부터** 트는 자리다 — 큐도 그 자리부터다.
            // 0 을 주면 지나간 큐가 `firedCues` 에 안 들어가 **몰려 뜬다**(`OfferCues.arm` 의 ★).
            this.offerCards.reset();
            this.offerCards.arm(item, startAtSec);
            this.emit('itemChanged', item, this.index);
            this.send('start', item);
            void this.armNext(gen);
            return;
        }
        // `resume` — **복귀에서 `start` 를 내지 않는다.** 회차가 새로 시작한 것이 아니고,
        // 광고 전에 이미 나갔다. 또 내면 시작이 둘이 되어 완료율의 분모가 부풀려진다.
        this.elapsedSec = target.at ?? 0;
        this.setState('playing');
        void this.armNext(gen);
    }
    /** `returnTo` → 무엇을 적재할 것인가 */
    returnTarget(returnTo) {
        if (returnTo.type === 'advance') {
            const nextIndex = (this.index + 1) % Math.max(1, this.items.length);
            const next = this.index === this.items.length - 1 && this.pendingNextRound
                ? this.pendingNextRound.items[0]
                : this.items[nextIndex];
            return { url: next?.videoUrl ?? null, at: undefined };
        }
        const item = this.getCurrentItem();
        if (!item)
            return { url: null, at: undefined };
        if (returnTo.type === 'startCurrent') {
            return { url: item.videoUrl, at: this.resumeSecFor(item.productId) };
        }
        // `live` 는 창이 아직 안 열렸을 때만 여기 온다 — 자른 자리로(라이브 스트림은 `armAdNext`·`closeAdBreak` 가 따로 고른다)
        return { url: item.videoUrl, at: returnTo.atSec ?? this.resumeSecFor(item.productId) };
    }
    markBreakEnded() {
        this.lastBreakEndedAtMs = Date.now();
        if (this.storage === null) {
            this.adGapInMemoryOnly = true;
            return;
        }
        try {
            this.storage.set(RECEIVER_LAST_AD_BREAK_KEY, String(this.lastBreakEndedAtMs));
            this.adGapInMemoryOnly = false;
        }
        catch {
            // 저장소가 막혔다(사파리 프라이빗). 메모리로 떨어지고 **그 사실을 드러낸다.**
            this.adGapInMemoryOnly = true;
        }
    }
    /** 노출과 시작을 함께 쏜다. **`Event` 로 가지 않는다** (절대 규칙 55) */
    trackAdStart(ad) {
        this.ads?.track(ad.tracking.impression);
        this.ads?.track(ad.tracking.start);
    }
    // ── v33. 라이브 바이패스 ──────────────────────────────────────────────
    /**
     * **화면이 라이브를 그리는 유일한 근거다** (절대 규칙 83 · `getAdBreak()` 와 같은 자리).
     *
     * 매 틱 바뀌는 값을 싣지 않는다 — 창 하나에 고정이므로 뷰에 실어도 리렌더를 만들지
     * 않는다(`AdBreakView.resumeAtSec` 와 갈리는 지점이 없다).
     */
    getLive() {
        const live = this.live;
        if (live === null)
            return null;
        return {
            eventId: live.window.eventId,
            title: live.window.title,
            endAtMs: live.window.endAtMs,
            resumeAtSec: live.returnTo?.atSec ?? null,
        };
    }
    isInLive() {
        return this.live !== null;
    }
    /**
     * ★ v34-b. **재생 화면의 「라이브 ↔ 이어보기」 버튼이 지금 어디로 데려가는가** — 편성표의 두
     * 칸(`docs/67` §9.4)을 편성표를 열지 않고 누르는 자리다. 부르는 문은 그대로 둘이다 —
     * `'recorded'` 면 `resumeRecorded()`, `'live'` 면 `joinLive()`.
     *
     * - 바이패스 중 → `'recorded'` (컷 자리로 · 절대 규칙 84)
     * - 열린 창이 있고 **시청자가 그 창을 두고 나왔다**(`skippedEventIds`) → `'live'`
     * - 그 밖 → `null` — 창이 없거나, **리시버가 곧 스스로 들어간다**(컷이 무장돼 다음 청크
     *   경계를 기다리는 1~2초 · 적재 중 · 503 뒤 재시도). 그때 `'live'` 를 내면 창이 열리는
     *   순간마다 버튼이 잠깐 섰다 이름을 바꾸고, 그 사이 누르면 **청크 경계가 아닌 자리**를
     *   들고 들어간다(`enterLiveNow` 의 즉시형).
     *
     * **시각에 따라 바뀐다**(창이 열리고 닫힌다) — 사건이 없으므로 앱이 쥔 1초 시계로 다시
     * 읽는다(`livePollIntervalMs` 와 같은 자리 · 코어에 타이머가 없다 · 절대 규칙 6).
     */
    /**
     * 2026-10-29 · **라이브 앞뒤 광고 중이고 채널이 녹화물을 잠갔다** — 회차 선택이 무동작이다(`recordedLockedByLiveAd` · 두 엔진이 같은
     * 함수를 부른다). 회차 건너뛰기는 광고 중이라 이미 없다.
     */
    recordedLocked() {
        return recordedLockedByLiveAd(this.adPolicy?.liveAdLockRecorded ?? false, this.adBreak?.kind);
    }
    liveSwitchTarget() {
        if (this.live !== null)
            return 'recorded';
        const { current } = liveWindowAt(this.liveWindows, this.liveNow());
        if (current === null)
            return null;
        return this.skippedEventIds.has(current.eventId) ? 'live' : null;
    }
    /**
     * ★ **서버 시계로 맞춘 지금** (`docs/67` §7.4 · D17).
     *
     * 기기 시계를 그대로 쓰면 **데모 노트북이 3분 틀어져 있을 때 라이브가 3분 늦게
     * 시작하고 증상이 조용하다.** 보정값은 플레이리스트 응답의 `generatedAt` 에서 온다 —
     * 새 요청도 새 필드도 없다(`PlaylistClient.serverSkewMs` 의 ★★).
     */
    liveNow() {
        return Date.now() + this.playlist.serverSkewMs;
    }
    /**
     * 바이패스에 들어갈 때인가. **`onTimeUpdate` 축(약 4Hz)에서 돈다** — 예약형 광고와
     * **같은 축이고 같은 모양**이다(`maybeOpenScheduledBreak`).
     *
     * ── 갈래 둘 ─────────────────────────────────────────────────
     *
     * ① **광고가 화면에 있다** → 즉시형. 광고 버퍼에 컷을 걸 수 없고 pod 은 버린다(D6)
     * ② **본편이 화면에 있다** → 라이브 버퍼를 적재하고, **끝나면** 미드롤과 물리적으로
     *    같은 컷을 무장한다(`prepareLiveCut`)
     *
     * ★ v33-d. **창이 열리기 전에는 아무것도 하지 않는다** (2026-09-23 · `docs/68` §2.0-e).
     * 창 전의 스트림 주소는 503 이다(`docs/67` §5.2) — 미리 적재할 것이 없다.
     */
    maybeEnterLive() {
        const gen = this.gen;
        const nowMs = this.liveNow();
        // v37. **판정은 `liveEntryPlan` 하나다**(`core/live-entry.ts` · 절대 규칙 99) — Flower 엔진이
        // 같은 표(벡터)로 맞춘다. 무장한 컷을 앞지르지 않는 것 · 대기 상한 · 회차 끝 미룸이 거기 있다.
        const plan = liveEntryPlan({
            nowMs,
            windows: this.liveWindows,
            skippedEventIds: this.skippedEventIds,
            state: this.state,
            inLive: this.live !== null,
            adOnScreen: this.adBreak !== null,
            // 2026-10-29 · 시작 전 광고를 묻는 중이거나 받아 두고 컷을 기다리면 그것이 먼저다(`docs/45` §18)
            adLeadsToLive: this.adBreak?.returnTo.type === 'live',
            adPending: this.pendingAd !== null || this.liveBeforeInFlight || this.liveBeforePod !== null,
            cut: this.liveCut,
            retryAtMs: this.liveCutRetryAtMs,
            item: this.getCurrentItem(),
            elapsedSec: this.elapsedSec,
        });
        if (plan.window === null)
            return;
        if (plan.action === 'enter-now')
            void this.enterLiveNow(gen, plan.window);
        else if (plan.action === 'prepare')
            this.prepareLiveCut(gen, plan.window, nowMs);
    }
    /**
     * ★ v33-d. **라이브 버퍼를 먼저 적재하고, 끝나야 컷을 무장한다** (`docs/68` §2.0-e).
     *
     * 꼬리 스왑은 대기 버퍼가 준비됐는지 묻지 않고 **그 자리에서 바꾼다** — 자연 전환에서는
     * 다음 회차가 늘 먼저 적재돼 있어 문제가 없었다. 그러니 **준비된 뒤에만 무장하는 것이
     * 이쪽의 몫이다.** 실패하면 무장하지 않고 본편을 계속 간다 — 다음 틱이 다시 시도한다
     * (`docs/67` §7.4 가 적은 「적재가 실패해 본편을 계속 간다」는 이것으로 비로소 참이다).
     *
     * **회차 끝이 가까우면 기다린다.** 적재가 대기 버퍼를 라이브로 갈아 끼우므로 그 사이에
     * 회차가 끝나면 **자연 전환이 라이브로 넘어간다.** 다음 회차가 시작된 뒤에 자른다 —
     * 합류가 몇 초 늦을 뿐이다. (v37 — 그 판정은 `liveEntryPlan` 의 `defer` 이고, 적재 뒤
     * 무장해도 되는지는 `liveArmAllowed` 다 · `core/live-entry.ts`.)
     */
    prepareLiveCut(gen, window, nowMs) {
        const index = this.index;
        const cut = { eventId: window.eventId, phase: 'preparing', sinceMs: nowMs };
        this.liveCut = cut;
        this.player.prepareNext(window.streamUrl).then(() => {
            if (gen !== this.gen || this.liveCut !== cut)
                return;
            // 기다리는 사이에 무엇이 바뀌었으면 **이 적재는 버린다** — 다음 틱이 다시 판단한다.
            if (!liveArmAllowed({
                inLive: this.live !== null,
                adOnScreen: this.adBreak !== null,
                adPending: this.pendingAd !== null,
                sameItem: this.index === index,
                state: this.state,
            })) {
                this.liveCut = null;
                return;
            }
            this.liveCut = { eventId: window.eventId, phase: 'armed', sinceMs: this.liveNow() };
            this.armLiveCut(gen, window);
        }, () => {
            if (this.liveCut !== cut)
                return;
            this.liveCut = null;
            this.liveCutRetryAtMs = this.liveNow() + LIVE_PREPARE_RETRY_MS;
        });
    }
    /**
     * ★ **미드롤과 물리적으로 같다** (`docs/67` §7.2 · 절대 규칙 84).
     *
     * 정책상 초는 **콘텐츠 축의 지금 경과**다 — 창은 이미 열렸다. 어댑터가 그 값을
     * **청크 경계로 올리고**(`adCutAnchor` 가 `alignToChunkStart(...).endSec` 을 낸다),
     * 올라간 값이 그대로 복귀 지점이다(왕복 불변식 · 새 산술 0).
     *
     * **`complete` 를 내지 않는다** — 회차가 끝난 것이 아니다(`armPendingBreak` 의
     * `atEpisodeEnd` 판정과 같은 근거).
     */
    armLiveCut(gen, window) {
        this.player.armTailSwap({
            prerollSec: this.prerollSec,
            atSec: this.elapsedSec,
            onSwapped: (_path, cutSec) => {
                if (gen !== this.gen)
                    return;
                this.enterLive(gen, window, cutSec ?? null);
            },
            onAbandoned: () => {
                // 버려졌으면 **다시 준비할 수 있어야 한다** — 그러지 않으면 즉시형만 남는다.
                this.liveCut = null;
            },
        });
    }
    /** 컷이 일어났다 — 라이브가 화면에 있다 */
    enterLive(gen, window, cutAtSec) {
        if (gen !== this.gen)
            return;
        const item = this.getCurrentItem();
        this.openLive(window, item === null || cutAtSec === null ? null : { productId: item.productId, atSec: cutAtSec });
    }
    /**
     * 이미 창 안이라 **즉시** 바이패스로 간다 (`startPendingBreakNow` 와 같은 모양).
     *
     * 여기서는 컷이 없으므로 **돌아갈 자리가 지금 경과**다 — 화면에 콘텐츠가 있었다면
     * 그 초이고, 아직 아무것도 안 틀었으면 `null` 이다(D8).
     */
    async enterLiveNow(gen, window) {
        const item = this.getCurrentItem();
        /*
         * 2026-10-29 — 광고가 화면에 있으면 `elapsedSec` 은 **광고의 초**다. 미드롤이면 돌아갈 자리(자른 초)를 들고 간다 — 그 밖의
         * 브레이크(프리롤·회차 사이)는 돌아갈 본편 자리가 없다(이어보기 지점으로 · `liveReturnTarget`).
         */
        const breakReturn = this.adBreak?.returnTo ?? null;
        const atSec = breakReturn === null
            ? this.elapsedSec
            : breakReturn.type === 'resume' || breakReturn.type === 'live'
                ? breakReturn.atSec
                : null;
        const returnTo = item === null || this.state !== 'playing' || atSec === null
            ? null
            : { productId: item.productId, atSec };
        this.player.cancelTailSwap();
        this.openLive(window, returnTo);
        try {
            await this.player.load(window.streamUrl);
            await this.player.play();
        }
        catch (e) {
            // 앞지름당한 실패는 화면을 덮지 않는다 — 근거는 `start()` 의 ★.
            if (gen !== this.gen)
                return;
            /*
             * ★ **창이 아직 열리지 않았으면 서버가 503 이다**(`docs/67` §7.4 의 「일찍 붙는 것은
             * 서버가 막는다」). 오류를 내지 않고 **바이패스를 접는다** — 다음 틱에 다시 시도한다.
             */
            this.closeLive();
            void e;
        }
    }
    /** 상태를 세우고 광고·카드를 걷는다. **진입 경로 둘이 공유한다** */
    openLive(window, returnTo) {
        /*
         * ★ **라이브가 이긴다** (`docs/67` D6 · Q2 의 짝). pod 을 끝까지 틀면 「라이브인데
         * 광고가 먼저」가 되고, 그 지각은 「광고가 하나 덜 나갔다」보다 나쁘다 — 규칙 51
         * (광고는 재생을 멈추지 않는다)의 대칭이다.
         *
         * **`markBreakEnded()` 를 남긴다** — 그래야 `minGapSec` 이 살아 복귀 직후에 광고가
         * 곧바로 또 열리지 않는다(v21/F13 이 값을 치른 그 자리).
         */
        if (this.adBreak !== null) {
            this.markBreakEnded();
            this.adBreak = null;
            this.emit('adBreakChanged', null);
        }
        this.pendingAd = null;
        // v23. **카드를 먼저 걷는다** (절대 규칙 64 의 모양 — 라이브 위에 상품 카드가 겹치면
        // 그것은 제휴사에게 오염된 노출이고 화면에는 「좀 지저분하다」로만 보인다).
        this.offerCards.close();
        this.liveCut = null;
        this.live = { window, returnTo, returnArmed: false };
        this.elapsedSec = 0;
        this.setState('playing');
        this.emit('liveChanged', this.getLive());
    }
    /**
     * ★ v33-b. **편성표에서 라이브 상자를 눌렀다** — 이번 창에 합류한다 (`docs/67` §9.4).
     *
     * 한 채널 안에서 **라이브와 개인화 목록 사이를 오갈 수 있다**는 것이 이 문의 뜻이다.
     * 목록을 고르면 그 창을 포기하는데(`selectItem` 의 ★★), **포기는 되돌릴 수 있어야
     * 한다** — 아니면 한 번 고른 뒤로는 그 창에 영영 못 들어간다(채널을 다시 켜야 한다).
     *
     * **라이브는 방송이다** — 합류하면 지금 이 순간부터다. 자리를 되찾아 주지 않는다.
     * 대신 지금 보던 녹화 지점을 `returnTo` 로 들고 들어가므로, 나올 때 **거기서 이어본다**
     * (`enterLiveNow` 가 그 일을 이미 한다).
     */
    async joinLive() {
        const { current } = liveWindowAt(this.liveWindows, this.liveNow());
        if (current === null)
            return;
        // 포기를 되돌린다 — 이것이 이 문이 하는 일의 절반이다.
        this.skippedEventIds.delete(current.eventId);
        if (this.live !== null)
            return;
        const gen = ++this.gen;
        await this.enterLiveNow(gen, current);
    }
    /**
     * ★ v33-b. **편성표에서 지금 보던 녹화 회차를 눌렀다** — 창을 포기하고 컷 자리로
     * 돌아온다 (`docs/67` §9.4).
     *
     * `selectItem()` 으로 대신하지 말 것 — 그것은 **언제나 처음부터**다(절대 규칙 68).
     * 시청자가 누른 것은 「내가 보던 그 자리」이고, 창이 끝났을 때 저절로 돌아가는 자리와
     * **같은 값이어야 한다**(절대 규칙 84 — 「자른 곳」과 「돌아온 곳」이 두 벌이 되면
     * 차이가 0~2초라 아무도 신고하지 않는다).
     *
     * **미리 적재하고 나간다** — 창이 끝나기 전이라 복귀 버퍼가 아직 무장돼 있지 않다.
     * 안 하면 `swapNow('live')` 가 빈 버퍼로 가고 `leaveLive` 의 폴백(`load` + `play`)이
     * 받는데, 그 갈래는 **프리즈가 길다.**
     */
    async resumeRecorded() {
        const live = this.live;
        if (live === null)
            return;
        this.skippedEventIds.add(live.window.eventId);
        const gen = this.gen;
        const target = this.liveReturnTarget(live);
        if (target.url !== null && !live.returnArmed) {
            live.returnArmed = true;
            try {
                await this.player.prepareNext(target.url, target.at);
            }
            catch {
                // 폴백이 받는다.
            }
        }
        if (gen !== this.gen)
            return;
        await this.leaveLive(gen, live);
    }
    /**
     * ★ v33-d. **보조 시계 — 앱이 1초마다 부른다** (2026-09-23 · `docs/68` §2.0-e).
     *
     * 바이패스를 **나오는** 판정은 `onTimeUpdate` 축에서 도는데, 그 축은 **화면에 있는
     * 버퍼가 재생될 때만** 돈다. 라이브가 멈추면(빈 버퍼 · 망 끊김) 축도 멈추고, 그러면
     * **창이 끝나도 영영 돌아오지 않는다** — 「재생이 멈춰 있으면 바이패스도 필요 없다」
     * (`docs/67` §7.1)는 **멈춘 것이 라이브 자신일 때 거짓이다.**
     *
     * **나오는 쪽만 돈다.** 들어가는 쪽을 여기서 돌리면 멈춘 본편·숨은 탭에서 라이브가
     * 켜진다. 코어에 타이머가 없으므로(절대 규칙 6) 시계는 앱이 쥔다(`useTv2m` 의 하트비트와
     * 같은 모양).
     */
    checkLive() {
        this.maybeLeaveLive();
    }
    /**
     * ★ v34. **창 목록을 조회해 갈아 끼운다** — 앱의 시계가 `livePollIntervalMs()` 마다 부른다
     * (`docs/69` §5.2 · 절대 규칙 87). 리시버는 큐를 모른다: 창의 시각이 바뀌면 **진입은 다음
     * `timeupdate` 가**, 이탈은 여기 끝의 `maybeLeaveLive()` 가 한다 — 둘 다 v33 의 그 경로다.
     *
     * 실패는 조용하다 — 다음 조회가 받는다(창이 늦게 바뀔 뿐 재생에는 아무 일도 없다).
     */
    async refreshLive() {
        let windows;
        try {
            windows = await this.playlist.fetchLive();
        }
        catch {
            return;
        }
        // 그 사이에 멈췄으면(채널을 떠났다) 받지 않는다 — 다음 채널이 옛 창을 물지 않게.
        if (this.items.length === 0)
            return;
        this.applyLiveWindows(windows);
    }
    /** 지금 들고 있는 창들 — 편성표가 지금 채널의 행을 그린다(주소는 앱이 뗀다 · 규칙 68) */
    getLiveWindows() {
        return this.liveWindows;
    }
    /**
     * v34. 다음 조회까지 기다릴 시간(ms) · `null` 이면 조회하지 않는다(창이 없는 채널).
     * 계약의 `livePollIntervalMs` 그대로다 — 앱은 이 값만 본다.
     */
    livePollIntervalMs() {
        return livePollIntervalMs(this.liveWindows, this.liveNow(), this.live !== null);
    }
    /**
     * 창 목록을 통째로 갈아 끼운다. **바이패스 중인 이벤트는 창만 바꾸고 `returnTo` 를 지킨다**
     * (v33 D26 의 `resync` 그 모양 · `docs/69` R6) — 비우면 비로그인 시청자의 복귀 지점이 사라진다.
     */
    applyLiveWindows(windows) {
        this.liveWindows = windows;
        const live = this.live;
        if (live !== null) {
            const fresh = windows.find((w) => w.eventId === live.window.eventId);
            if (fresh !== undefined)
                live.window = fresh;
        }
        this.emit('liveWindowsChanged', windows);
        this.maybeLeaveLive();
    }
    /**
     * 바이패스에서 나올 때인가. **같은 축에서 돈다.**
     *
     * ① 끝 `AD_REQUEST_LEAD_SEC` 전에 **복귀 버퍼를 적재한다**(광고 pod 의 마지막 광고와
     *    같은 자리) ② 끝났으면 **즉시 전환**한다
     */
    maybeLeaveLive() {
        const live = this.live;
        if (live === null)
            return;
        const nowMs = this.liveNow();
        /*
         * v34. **닫혔는가는 `liveEffectiveWindow` 가 말한다**(절대 규칙 86) — 시각 방식이면 v33 과
         * 같고(끝 = `endAtMs`), 큐 방식이면 종료 큐를 받은 순간이다. 창 목록에서 **사라졌으면**
         * (지웠다 · 꺼졌다 · 끝나서 선별에서 빠졌다) 그것도 닫힌 것이다.
         */
        const eff = liveEffectiveWindow(live.window, nowMs);
        const listed = this.liveWindows.some((w) => w.eventId === live.window.eventId);
        const open = listed && isLiveOpen(eff.phase);
        /*
         * ★ v34. **복귀 버퍼를 언제 적재하는가** (절대 규칙 88 · `docs/69` §5.3).
         * 시각 종료는 v33 그대로 끝 `AD_REQUEST_LEAD_SEC` 앞이다. **열린 종료(큐 종료)는 끝을 미리
         * 모르므로 바이패스에 들어간 직후** — 바이패스 동안 대기 버퍼는 놀고 있고, 안 하면 종료 큐가
         * 오는 순간 빈 버퍼로 교체되어 폴백(프리즈가 긴 갈래)이 받는다.
         */
        const remainSec = eff.endMs === null ? Infinity : (eff.endMs - nowMs) / 1000;
        const armNow = eff.openEnd || remainSec <= AD_REQUEST_LEAD_SEC || !open;
        // 2026-10-29 · 종료 후 광고(`docs/45` §18) — 시각 종료면 끝 `AD_REQUEST_LEAD_SEC` 전에 미리 묻는다. 열린 종료(큐)는 닫히는 순간에 묻는다
        const afterSec = this.liveAfterSec();
        if (afterSec > 0 && open && !eff.openEnd && remainSec <= AD_REQUEST_LEAD_SEC)
            this.askLiveAfter(live, false);
        if (!live.returnArmed && armNow) {
            live.returnArmed = true;
            const target = this.liveReturnTarget(live);
            if (target.url !== null) {
                void this.player.prepareNext(target.url, target.at).catch(() => undefined);
            }
        }
        if (open)
            return;
        if (afterSec > 0) {
            void this.leaveLiveAfter(this.gen, live);
            return;
        }
        void this.leaveLive(this.gen, live);
    }
    // ── 2026-10-29 · 라이브 이벤트 앞뒤 광고 (`docs/45` §18) ───────────────────
    /** 창이 지금 열려 있는가 — 목록에 있고 실효 창이 열렸다(`maybeLeaveLive` 와 같은 판정) */
    liveWindowOpen(window) {
        const fresh = this.liveWindows.find((w) => w.eventId === window.eventId);
        if (fresh === undefined)
            return false;
        return isLiveOpen(liveEffectiveWindow(fresh, this.liveNow()).phase);
    }
    liveAfterSec() {
        const policy = this.adPolicy;
        return policy === null || this.ads === null ? 0 : policy.liveAfterSec;
    }
    /**
     * 라이브 앞뒤 광고의 요청. 브레이크의 관문(`openBreak`)을 지나지 않는다 — 최소 간격·재귀 금지는 회차의 브레이크끼리의 규칙이고, 이
     * 둘은 **이벤트에 매인** 자리다(시작·종료에 한 번씩). 실패·no fill 은 조용하다(빈 배열).
     */
    requestLivePod(kind, maxDurationSec, timeoutMs) {
        const policy = this.adPolicy;
        if (policy === null || this.ads === null || maxDurationSec <= 0)
            return Promise.resolve([]);
        const current = this.getCurrentItem();
        this.correlator += 1;
        return this.ads.requestAds({
            adServerUrl: policy.adServerUrl,
            channelId: this.playlist.servedChannelId ?? '',
            breakKind: kind,
            maxDurationSec,
            productId: current?.productId ?? '',
            positionSec: this.live === null ? Math.max(0, Math.floor(this.elapsedSec)) : 0,
            language: policy.language ?? 'ko',
            deviceId: this.deviceId,
            playlistId: this.playlist.playlistId ?? '',
            correlator: this.correlator,
        }, { timeoutMs });
    }
    /**
     * **라이브 시작 전 광고** — `onTimeUpdate` 축에서 진입 판정 앞에 돈다. 때는 `liveBeforePlan` 이 정한다(규칙 95).
     *
     * 시작 전 광고가 화면에 있고 그 마지막 광고가 도는 중에 창이 열렸으면 **대기 버퍼를 라이브 스트림으로 다시 무장한다** — 창 전의
     * 스트림은 503 이라 그 광고가 시작할 때는 자른 자리를 물고 있었다(`armAdNext`). 끝이 3초 안이면 두고, 끝난 뒤 라이브를 직접 적재한다.
     */
    maybeLiveBeforeBreak() {
        const gen = this.gen;
        const shown = this.adBreak;
        if (shown !== null) {
            if (shown.returnTo.type !== 'live' || shown.liveArmed || shown.skipping)
                return;
            const last = shown.items[shown.index];
            if (shown.index !== shown.items.length - 1 || last === undefined)
                return;
            if (last.durationSec - this.elapsedSec < REFILL_REARM_LEAD_SEC)
                return;
            if (this.liveWindowOpen(shown.returnTo.window))
                this.armAdNext(gen);
            return;
        }
        const policy = this.adPolicy;
        if (policy === null || this.ads === null || this.liveWindows.length === 0)
            return;
        const item = this.getCurrentItem();
        const pod = this.liveBeforePod;
        const plan = liveBeforePlan({
            nowMs: this.liveNow(),
            windows: this.liveWindows,
            skippedEventIds: this.skippedEventIds,
            askedEventIds: this.liveBeforeAsked,
            pod: pod === null
                ? null
                : {
                    eventId: pod.eventId,
                    totalSec: pod.items.reduce((sum, ad) => sum + ad.durationSec, 0),
                },
            breakSec: policy.liveBeforeSec,
            onContent: this.state === 'playing' &&
                this.live === null &&
                this.pendingAd === null &&
                this.liveCut === null &&
                !this.liveBeforeInFlight &&
                item !== null &&
                // 회차 끝이 가까우면 다음 회차에서 자른다 — 꼬리에서는 컷의 앵커를 못 구한다(`armTailSwap` 의 규약 ②)
                (item.durationSec <= 0 || item.durationSec - this.elapsedSec > LIVE_BEFORE_ITEM_TAIL_SEC),
        });
        // 버리는 것은 창이 없어도 버린다 — 그 이벤트가 목록에서 사라졌을 때다(남기면 `adPending` 이 다음 진입을 영원히 막는다)
        if (plan.action === 'drop') {
            this.liveBeforePod = null;
            return;
        }
        const window = plan.window;
        if (window === null)
            return;
        if (plan.action === 'request') {
            this.liveBeforeAsked.add(window.eventId);
            this.liveBeforeInFlight = true;
            void this.requestLivePod('live_before', policy.liveBeforeSec, plan.blocking ? AD_REQUEST_BLOCKING_TIMEOUT_MS : AD_REQUEST_TIMEOUT_MS).then((items) => {
                this.liveBeforeInFlight = false;
                if (gen !== this.gen || items.length === 0)
                    return;
                this.liveBeforePod = { eventId: window.eventId, items };
            });
            return;
        }
        if (plan.action === 'cut' && pod !== null) {
            this.liveBeforePod = null;
            const first = pod.items[0];
            if (first === undefined)
                return;
            const pending = {
                kind: 'live_before',
                atSec: this.elapsedSec,
                items: pod.items,
                returnTo: { type: 'live', window, atSec: null },
            };
            this.pendingAd = pending;
            /*
             * ★ **첫 광고를 다 올린 뒤에 컷을 건다**(라이브 컷의 v33-d 와 같은 교훈 · `docs/68` §2.0-e). 컷은 지금 경과를 넘어선 첫 청크
             * 경계라 0~2초 앞이다 — 미드롤처럼 20초 전에 올려 두지 않았으므로, 바로 걸면 대기 버퍼가 첫 프레임을 내기 전에 스왑이 와서
             * 재생 오류가 난다(로컬 실측 2026-10-29: 0.29초 앞의 컷 → 광고가 화면에 오기 전의 오류라 화면을 덮었다). 올린 뒤 **그때의
             * 경과**로 자르고, 그 초가 라이브가 끝난 뒤 돌아올 자리다(규칙 84).
             */
            this.player.prepareNext(first.videoUrl).then(() => {
                if (gen !== this.gen || this.pendingAd !== pending)
                    return;
                pending.atSec = this.elapsedSec;
                this.armPendingBreak(gen);
            }, () => {
                if (this.pendingAd === pending)
                    this.pendingAd = null;
            });
        }
    }
    /** 시작 전 광고가 끝났고 창이 열렸다 — 그 라이브로. `onStream` 이면 마지막 광고의 꼬리 스왑이 이미 라이브를 화면에 올렸다 */
    async enterLiveAfterBreak(gen, returnTo, onStream) {
        const item = this.getCurrentItem();
        const back = item === null || returnTo.atSec === null
            ? null
            : { productId: item.productId, atSec: returnTo.atSec };
        this.openLive(returnTo.window, back);
        if (onStream)
            return;
        try {
            await this.player.load(returnTo.window.streamUrl);
            await this.player.play();
        }
        catch (e) {
            if (gen !== this.gen)
                return;
            // 라이브를 열지 못했다 — 자른 자리로 돌아간다(다음 틱이 평소 진입을 다시 판단한다)
            this.closeLive();
            if (item === null)
                return;
            try {
                await this.player.load(item.videoUrl, back?.atSec);
                await this.player.play();
            }
            catch (e2) {
                if (gen !== this.gen)
                    return;
                this.fail({
                    code: 'PLAYBACK_FAILED',
                    message: '라이브 시작 전 광고 뒤 복귀가 실패했다.',
                    cause: e2,
                });
                return;
            }
            this.elapsedSec = back?.atSec ?? 0;
            this.setState('playing');
            void this.armNext(gen);
            void e;
        }
    }
    /** 종료 후 광고를 묻는다(이벤트마다 한 번). 답이 오면 첫 광고를 대기 버퍼에 올린다 — 창이 닫히는 순간 그리로 넘긴다 */
    askLiveAfter(live, blocking) {
        const eventId = live.window.eventId;
        if (this.liveAfterAsked.has(eventId))
            return;
        this.liveAfterAsked.add(eventId);
        const gen = this.gen;
        const run = this.requestLivePod('live_after', this.liveAfterSec(), blocking ? AD_REQUEST_BLOCKING_TIMEOUT_MS : AD_REQUEST_TIMEOUT_MS).then((items) => {
            if (gen !== this.gen || this.live !== live || items.length === 0)
                return;
            this.liveAfterPod = { eventId, items };
            const first = items[0];
            if (first)
                void this.player.prepareNext(first.videoUrl).catch(() => undefined);
        });
        const tracked = run.finally(() => {
            if (this.liveAfterRequest === tracked)
                this.liveAfterRequest = null;
        });
        this.liveAfterRequest = tracked;
    }
    /**
     * **라이브 종료 후 광고** — 창이 닫혔다. 답(미리 물었으면 그것 · 아니면 지금 묻고 최대 즉시형 타임아웃)을 듣고 광고가 있으면 그리로,
     * 없으면 평소 복귀(`leaveLive`)다. 광고가 끝나면 녹화물의 컷한 자리로 돌아간다(`fromLive`). 시청자가 스스로 나온 것(「이어보기」)에는
     * 없다 — 그 길은 `resumeRecorded` 가 `leaveLive` 를 바로 부른다.
     */
    async leaveLiveAfter(gen, live) {
        if (live.leaving)
            return;
        live.leaving = true;
        this.askLiveAfter(live, true);
        if (this.liveAfterRequest !== null)
            await this.liveAfterRequest;
        if (gen !== this.gen || this.live !== live)
            return;
        const pod = this.liveAfterPod?.eventId === live.window.eventId ? this.liveAfterPod : null;
        this.liveAfterPod = null;
        const first = pod?.items[0];
        if (pod === null || first === undefined) {
            await this.leaveLive(gen, live);
            return;
        }
        await this.player.prepareNext(first.videoUrl).catch(() => undefined);
        if (gen !== this.gen || this.live !== live)
            return;
        const target = this.liveReturnTarget(live);
        this.closeLive();
        try {
            await this.player.swapNow('live');
        }
        catch {
            // 광고로 넘기지 못했다 — 광고를 버리고 녹화물로 직접 돌아간다(`leaveLive` 의 폴백과 같은 자리)
            if (gen !== this.gen)
                return;
            const item = this.getCurrentItem();
            if (item === null || target.url === null)
                return;
            try {
                await this.player.load(target.url, target.at);
                await this.player.play();
            }
            catch (e) {
                if (gen !== this.gen)
                    return;
                this.fail({ code: 'PLAYBACK_FAILED', message: '라이브 뒤 복귀가 실패했다.', cause: e });
                return;
            }
            this.elapsedSec = target.at ?? 0;
            this.setState('playing');
            this.emit('itemChanged', item, this.index);
            if (live.returnTo === null)
                this.send('start', item);
            void this.armNext(gen);
            return;
        }
        if (gen !== this.gen)
            return;
        this.pendingAd = {
            kind: 'live_after',
            atSec: null,
            items: pod.items,
            returnTo: { type: 'fromLive', atSec: target.at, sendStart: live.returnTo === null },
        };
        this.enterAdBreak(gen, null);
    }
    /**
     * ★★ **복귀는 꼬리 스왑이 아니라 `swapNow('live')` 다** (절대 규칙 84 · `docs/67` §7.3).
     *
     * 라이브 매니페스트에는 끝이 없다 — `armTailSwap({atSec})` 이 쓰는 세 값
     * (`segmentDurations`·`videoStart`·`videoSpan`)이 **창(약 20초)의 것**이라 무장하면
     * **붙자마자 스왑이 터진다.** 어댑터가 라이브 버퍼에서 그 셋을 비워 두므로(v33 LV5)
     * 실수로 불러도 조용히 거르지만, **부르지 않는 것이 이쪽의 몫이다.**
     *
     * 대가는 프리즈다 — 대기 버퍼가 적재돼 있고 재생 중은 아니므로 `no-preroll`(S2 ≈ 67ms).
     * **두 시간에 한 번이다.**
     */
    async leaveLive(gen, live) {
        if (gen !== this.gen)
            return;
        const target = this.liveReturnTarget(live);
        if (target.url === null) {
            this.closeLive();
            return;
        }
        this.closeLive();
        try {
            await this.player.swapNow('live');
        }
        catch {
            // 스왑이 안 되면 직접 적재한다 — `closeAdBreak` 의 `needsLoad` 갈래와 같은 자리다.
            try {
                await this.player.load(target.url, target.at);
                await this.player.play();
            }
            catch (e) {
                if (gen !== this.gen)
                    return;
                this.fail({ code: 'PLAYBACK_FAILED', message: '라이브 뒤 복귀가 실패했다.', cause: e });
                return;
            }
        }
        if (gen !== this.gen)
            return;
        const item = this.getCurrentItem();
        if (item === null)
            return;
        /*
         * ★ **두 갈래이고 `start` 의 유무가 갈린다** (`closeAdBreak` 의 그 모양).
         *
         * ① `returnTo` 가 있다 — **회차가 새로 시작한 것이 아니다.** 바이패스 전에 이미
         *    `start` 가 나갔으므로 또 내면 완료율의 분모가 부풀려진다.
         * ② `returnTo` 가 없다(이벤트 중 진입) — 그 회차를 **정말로 처음 트는 것**이라 낸다.
         */
        this.elapsedSec = target.at ?? 0;
        this.setState('playing');
        // v23. 큐는 **적재한 자리부터** 무장한다 — 0 을 주면 지나간 큐가 몰려 뜬다.
        this.offerCards.reset();
        this.offerCards.arm(item, this.elapsedSec);
        this.emit('itemChanged', item, this.index);
        if (live.returnTo === null)
            this.send('start', item);
        void this.armNext(gen);
    }
    /**
     * `returnTo` → 무엇을 적재할 것인가. **`returnTarget()` 의 짝이다.**
     *
     * ★ **`resumeSecFor` 를 다시 부르지 않는다** (절대 규칙 84). 「자른 곳」과 「돌아온 곳」이
     * 두 벌이 되고, 차이가 0~2초라 **아무도 신고하지 않는다.** 값은 컷이 쓴 것 하나다.
     */
    liveReturnTarget(live) {
        const item = this.getCurrentItem();
        if (item === null)
            return { url: null, at: undefined };
        if (live.returnTo === null) {
            // 이벤트 중에 들어왔다 — 평소 진입과 같다(서버의 이어보기 지점).
            return { url: item.videoUrl, at: this.resumeSecFor(item.productId) };
        }
        return { url: item.videoUrl, at: live.returnTo.atSec };
    }
    /** 상태만 내린다. **화면이 먼저 알아야** 컨트롤 바가 한 프레임도 어긋나지 않는다 */
    closeLive() {
        if (this.live === null)
            return;
        this.live = null;
        this.emit('liveChanged', null);
    }
    // ── v23. 상품 오버레이 ────────────────────────────────────────────────
    /** 이 채널의 상품 노출 정책. **`null` 이면 상품 큐가 없다** */
    get offerPolicy() {
        return this.playlist.offerPolicy;
    }
    /**
     * v23. 이 채널의 상품 노출 정책. **`null` 이면 상품 큐가 없다.**
     *
     * **요청에 실은 값이 아니라 응답의 값이다** — `getAdPolicy()` 와 같은 방침이고,
     * 셋 중 하나만 어긋나도 서버가 `null` 을 준다 (`docs/47` §5.2).
     *
     * ★ **화면이 이것을 보는 자리는 하나다** — 설정의 「상품 추천」 줄. 홈쇼핑 채널에는
     * 그 줄 자체가 없어야 하고(절대 규칙 66), **없는 것을 끌 수 있게 보이면 그것이 곧
     * 「홈쇼핑에도 상품이 있다」는 신호다.** 규칙 56 이 광고에 대해 정한 그 모양이다.
     */
    getOfferPolicy() {
        return this.offerPolicy;
    }
    /**
     * **화면이 상품 오버레이를 그리는 유일한 근거다** (절대 규칙 59). 판정은 `OfferCues` 가 갖는다.
     */
    getOffer() {
        return this.offerCards.view();
    }
    /** 시청자가 닫았거나 눌렀다. **그 큐는 다시 열리지 않는다** */
    dismissOffer() {
        this.offerCards.dismiss();
    }
    /** 카드가 걷히기까지 남은 초. 떠 있지 않으면 `0` */
    getOfferRemainingSec() {
        return this.offerCards.remainingSec();
    }
    /** 상품 쪽 진단 — `activeCount`(산술이 버린 큐의 수)가 여기 있다 */
    offerDebugSnapshot() {
        return this.offerCards.debugSnapshot();
    }
    /**
     * 광고 쪽 진단. **`minGapSec` 이 메모리로 떨어졌는지가 여기 드러난다** — 조용히
     * 우회하지 않는다는 것이 `RECEIVER_LAST_AD_BREAK_KEY` 주석의 약속이다.
     */
    adDebugSnapshot() {
        return {
            hasAdapter: this.ads !== null,
            policy: this.adPolicy,
            inBreak: this.adBreak !== null,
            pending: this.pendingAd !== null,
            firedCuePoints: [...this.firedCuePoints],
            lastBreakEndedAtMs: this.lastBreakEndedAtMs,
            gapInMemoryOnly: this.adGapInMemoryOnly,
            correlator: this.correlator,
        };
    }
    send(type, item) {
        const playlistId = this.playlist.playlistId;
        if (!playlistId)
            return;
        const event = {
            deviceId: this.deviceId,
            playlistId,
            type,
            positionSec: Math.max(0, Math.floor(this.elapsedSec)),
        };
        if (item)
            event.productId = item.productId;
        // 채널 (v9). **`opts.channelId` 가 아니라 응답의 값이다** — 다채널에서 이벤트가
        // 어느 채널의 것인지는 서버가 내준 목록이 정한다 (`docs/23` §8).
        if (this.playlist.servedChannelId)
            event.channelId = this.playlist.servedChannelId;
        this.events.enqueue(event);
        this.emit('eventSent', event);
    }
    setState(state) {
        /*
         * ★ v35. **상태 전이는 정지를 푼다** (절대 규칙 92). 이 함수를 부르는 자리는 전부 「화면에
         * 다른 것이 서는」 자리다 — 시작 · 스킵 · 선택 · 자연 전환 · 광고 진입·복귀 · 라이브
         * 진입·복귀 · 복구 · 정지(`stop`) · 오류. 자리마다 풀면 하나를 빠뜨린 경로에서만 **영상은
         * 도는데 버튼이 「재생」**이다. 같은 상태로의 전이(`'playing'` → `'playing'`)도 푼다 —
         * 자연 전환이 그 모양이다.
         */
        this.releasePause();
        if (this.state === state)
            return;
        this.state = state;
        this.emit('stateChanged', state);
    }
    releasePause() {
        if (!this.paused)
            return;
        this.paused = false;
        this.emit('pausedChanged', false);
    }
    fail(error) {
        this.setState('error');
        this.emit('error', error);
    }
    emit(event, ...args) {
        const set = this.listeners.get(event);
        if (!set)
            return;
        for (const cb of set) {
            cb(...args);
        }
    }
}
/**
 * `deviceId` 는 `StorageAdapter` 에 보관해 재방문 시 유지한다. 없으면 생성한다.
 * v36 — Flower 엔진(`FlowerReceiver`)도 이것을 부른다: 두 엔진이 같은 `fast.deviceId` 를 써야 이벤트·광고 `dev` 가 한 기기다.
 * 저장소를 주지 않으면 호출자가 준 값을 그대로 쓴다 — 이 경우 새로고침마다 값이 바뀔 수 있다.
 */
export function resolveDeviceId(given, storage) {
    if (!storage)
        return given || randomId();
    const stored = storage.get(RECEIVER_DEVICE_ID_KEY);
    if (stored)
        return stored;
    const id = given || randomId();
    storage.set(RECEIVER_DEVICE_ID_KEY, id);
    return id;
}
/**
 * `crypto.randomUUID` 는 코어가 참조할 수 없다(`lib: ES2022` 에 없다).
 * 식별자는 암호학적 보증이 필요한 값이 아니라 이벤트를 묶는 상관관계 키다.
 */
function randomId() {
    const rand = () => Math.floor(Math.random() * 0x100000000).toString(16);
    return `dev-${rand()}${rand()}`;
}
/**
 * 마지막 브레이크 종료 시각을 저장소에서 읽는다. **채널과 무관한 값이다.**
 * v36 — Flower 엔진도 이것을 읽어 넘긴다: 엔진을 오가도 `minGapSec` 이 한 값이다(`docs/72` §4.1).
 * 없거나 못 읽으면 `null` — 첫 브레이크는 언제나 열린다.
 */
export function readLastBreakEndedAt(storage) {
    if (!storage)
        return null;
    try {
        const raw = storage.get(RECEIVER_LAST_AD_BREAK_KEY);
        if (raw === null)
            return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=receiver.js.map